<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <!-- Header -->
    <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-4">
      <div class="min-w-0">
        <h2 class="text-2xl font-bold text-gray-800">Reflektion</h2>
        <p class="text-sm text-gray-500 mt-1">
          Aus Korrekturen wird eine Regel, aus der Regel ein Prompt-Entwurf.
          Gemessen und aktiviert wird er in Prompt versions.
        </p>

        <!-- Der Kreislauf als Kette von Pillen. Die vollen Sätze hängen in den
             Tooltips: sie werden EINMAL gebraucht, danach steht der Ablauf im
             Kopf, und ein Textblock schöbe die eigentliche Arbeit bei jedem
             Besuch nach unten. Dasselbe Muster wie auf der Versionsseite.

             ACHTUNG, warum die Pillen KEINE Ziffern tragen (anders als auf der
             Versionsseite): dort sind es drei Schritte und drei Abschnitte,
             hier fünf Schritte und vier Abschnitte — und sie decken sich
             nicht. Zwei Zählungen auf einem Schirm ergeben eine falsche
             Landkarte: wer "Schritt 3" sagt, meint dann entweder den
             Regelvorschlag oder die Vorschlagsliste. Die Pfeile tragen die
             Reihenfolge ohnehin; stattdessen heißt jeder Abschnitt so wie
             seine Pille, und die Kette wird damit zum Inhaltsverzeichnis. -->
        <div class="flex flex-wrap items-center gap-1.5 mt-3">
          <template v-for="(schritt, i) in KREISLAUF" :key="schritt.titel">
            <span v-if="i" class="text-gray-300 text-xs" aria-hidden="true">→</span>
            <!-- Klickbar wird durch FARBE angezeigt, nicht durch ein Icon:
                 zwei der fünf Schritte führen auf eine andere Seite, drei
                 stehen hier auf dieser. Ein Pfeilsymbol in der Pille geriete
                 zudem neben die Pfeile, die die Kette trennen. -->
            <BaseTooltip :text="schritt.hilfe" :oben="false">
              <component
                  :is="schritt.ziel ? 'router-link' : 'span'"
                  :to="schritt.ziel"
                  class="inline-flex items-center px-2 py-1 rounded-full border text-xs"
                  :class="schritt.ziel
                    ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                    : 'border-gray-200 bg-gray-50 text-gray-700 cursor-help'"
              >
                {{ schritt.titel }}
              </component>
            </BaseTooltip>
          </template>

          <BaseInfoIcon :text="HILFE_KREISLAUF" label="der Kreislauf" :oben="false" />
        </div>
      </div>

      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <!-- Sichtbares Label, kein blosses aria-label: der Platzhalter lautet
             genauso wie der Vorgabewert, also steht im Feld immer Text und der
             Platzhalter ist nie zu sehen. Ohne Beschriftung ist das wichtigste
             Eingabefeld der Seite ein Kasten mit einer Zeichenkette darin. -->
        <label class="text-sm text-gray-600" for="reflection-satz">Vergleichssatz</label>
        <!-- <select> und KEIN Freitext mehr. Das Argument für ein offenes Feld
             war, die Kommandozeile könne jeden Satz messen — es trägt nicht:
             die Liste kommt aus derselben Tabelle, aus der die Diagnose liest.
             Ein Name, der nicht in der Liste steht, hat keine Zeile in
             prompt_eval_messages, und die Diagnose darauf ist zwangsläufig
             leer. Freitext konnte also nur eines: Tippfehler erzeugen. -->
        <BaseTooltip
            text="Über diesen Satz rechnet die Diagnose, und aus genau seinen Korrekturen entsteht die Regel. Die Zahl in Klammern ist die Zahl seiner Mitglieder. Ein Satz ist eingefroren — er wächst nicht mit dem Bestand."
        >
          <select
              id="reflection-satz"
              v-model="setName"
              class="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              :disabled="loading || !evalSets.length"
              @change="reload"
          >
            <!-- Der Bestandsname, solange die Liste noch lädt oder ihn nicht
                 führt. Ohne diese Option zeigte das Feld beim Aufschlagen den
                 ERSTEN Satz an, während die Diagnose darunter über einen
                 anderen rechnet — eine Anzeige, die lügt, bis jemand klickt. -->
            <option v-if="unbekannterSatz || !evalSets.length" :value="setName">
              {{ setName || "—" }}
            </option>
            <option v-for="s in evalSets" :key="s.set_name" :value="s.set_name">
              {{ s.set_name }} ({{ s.n_messages }})
            </option>
          </select>
        </BaseTooltip>

        <BaseTooltip text="Einen neuen Satz aus den heutigen Korrekturen einfrieren">
          <BaseButton
              variant="neutral"
              size="icon"
              type="button"
              aria-label="Neuen Vergleichssatz anlegen"
              :disabled="creatingSet"
              @click="oeffneNeuenSatz"
          >
            <PlusIcon class="w-5 h-5" />
          </BaseButton>
        </BaseTooltip>

        <!-- ACHTUNG, warum dieser Satz nicht in einem Tooltip stehen darf: er
             trennt zwei Auskünfte, die sonst gleich aussehen. Ein unbekannter
             Name und ein Satz ohne Korrekturen führen BEIDE auf "keine
             korrigierten Zellen" — die eine Lage verlangt einen anderen Satz,
             die andere mehr Korrekturarbeit. Wer den Unterschied erst beim
             Überfahren sieht, sucht an der falschen Stelle weiter. -->
        <span
            v-if="unbekannterSatz"
            class="inline-flex items-center gap-1.5 text-xs text-amber-800 font-semibold"
        >
          <ExclamationTriangleIcon class="w-4 h-4 shrink-0 text-amber-600" aria-hidden="true" />
          <span>Diesen Vergleichssatz gibt es nicht</span>
          <BaseInfoIcon :text="HILFE_UNBEKANNTER_SATZ" label="unbekannter Vergleichssatz" ton="amber" :oben="false" />
        </span>

        <BaseTooltip text="Diagnose, Optimierer-Prompt und Vorschläge neu laden">
          <BaseButton
              variant="neutral"
              size="icon"
              type="button"
              aria-label="Seite neu laden"
              :aria-busy="loading ? 'true' : 'false'"
              :disabled="loading"
              @click="reload"
          >
            <ArrowPathIcon class="w-5 h-5" :class="loading ? 'animate-spin' : ''" />
          </BaseButton>
        </BaseTooltip>
      </div>
    </div>

    <!-- Neuer Vergleichssatz. Bis R7.9 gab es diesen Schritt nur auf der
         Kommandozeile — der Kreislauf, den diese Seite als geschlossen
         darstellt, stieg genau hier ins Terminal aus.

         ACHTUNG, warum das kein Modal ist: es ist keine Bestätigung, sondern
         eine Eingabe mit drei Feldern, deren Wirkung man vorher lesen können
         muss. Ein Modal legte sich zudem über die Liste der Sätze — und die
         ist genau das, was man beim Benennen daneben braucht. -->
    <div v-if="showNewSet" class="mb-4 border border-indigo-200 bg-indigo-50 rounded-lg p-3">
      <div class="flex items-center gap-1.5 mb-2">
        <span class="text-sm font-semibold text-gray-800">Neuen Vergleichssatz einfrieren</span>
        <BaseInfoIcon :text="HILFE_NEUER_SATZ" label="neuer Vergleichssatz" ton="grau" :oben="false" />
      </div>

      <div class="flex flex-wrap items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-600" for="reflection-neuer-satz">Name</label>
          <input
              id="reflection-neuer-satz"
              v-model="newSetName"
              type="text"
              placeholder="korrektur-02"
              class="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-600" for="reflection-ziel">Zielgröße</label>
          <input
              id="reflection-ziel"
              v-model.number="newSetSize"
              type="number"
              min="1"
              class="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-600" for="reflection-minref">mind. korrigierte Nachrichten</label>
          <input
              id="reflection-minref"
              v-model.number="newSetMinRef"
              type="number"
              min="0"
              class="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>

        <BaseButton
            variant="primary"
            type="button"
            :disabled="!!neuerSatzBlockGrund || creatingSet"
            @click="createSet"
        >
          {{ creatingSet ? "Friert ein …" : "Einfrieren" }}
        </BaseButton>
        <BaseButton variant="neutral" type="button" :disabled="creatingSet" @click="showNewSet = false">
          Abbrechen
        </BaseButton>
      </div>

      <!-- Die Merkliste als Grundstock. Sie tritt an die Stelle der ersten
           Charge: statt aller korrigierten Nachrichten kommen genau die
           gemerkten in den Satz, aufgefüllt wird wie sonst.

           Der Schalter steht auch dann da, wenn nichts gemerkt ist — sonst
           wäre die Funktion unsichtbar für jeden, der sie nicht kennt, und der
           Sperrgrund darunter sagt, wo man merkt. -->
      <div class="mt-3 pt-3 border-t border-indigo-200 flex flex-wrap items-center gap-2 text-sm">
        <label class="inline-flex items-center gap-2 text-gray-800">
          <input v-model="merklisteNutzen" type="checkbox" class="rounded border-gray-400" />
          Merkliste als Grundstock verwenden
        </label>
        <span
            v-if="merkAnzahl > 0"
            class="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-xs"
            :title="`Nachrichten-IDs: ${merkliste.join(', ')}`"
        >
          ★ {{ merkAnzahl }} gemerkt
        </span>
        <router-link
            to="/async/messages"
            class="text-xs text-indigo-700 underline hover:text-indigo-900"
        >
          In der Nachrichtenliste merken
        </router-link>
      </div>

      <p v-if="neuerSatzHinweis" class="mt-2 text-xs text-gray-600">
        {{ neuerSatzHinweis }}
      </p>

      <!-- Der Grund als sichtbarer Text, nicht als Tooltip am gesperrten Knopf:
           ein deaktivierter Knopf verschluckt in Chrome und Firefox jedes
           Mausereignis. Dieselbe Regel wie bei „Übernehmen“ weiter unten. -->
      <p v-if="neuerSatzBlockGrund" class="mt-2 text-xs text-amber-800">
        {{ neuerSatzBlockGrund }}
      </p>
    </div>

    <!-- Was beim letzten Einfrieren wirklich passiert ist — Zeile für Zeile.
         Als LISTE und nicht als Fließtext: seit dem Merklistenweg stehen hier
         id-genaue Auskünfte darüber, welche Nachricht NICHT mitgekommen ist,
         und der Satz ist danach nicht mehr zu ändern. -->
    <div
        v-if="letztesProtokoll.length"
        class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3"
    >
      <div class="text-xs font-semibold text-gray-700 mb-1">Protokoll des Einfrierens</div>
      <ul class="text-xs text-gray-700 space-y-0.5 font-mono">
        <li v-for="(zeile, zi) in letztesProtokoll" :key="zi">{{ zeile }}</li>
      </ul>
    </div>

    <div v-if="error" class="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
      {{ error }}
    </div>

    <div v-if="savedMessage" class="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
      {{ savedMessage }}
    </div>

    <div v-if="hinweis" class="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
      {{ hinweis }}
    </div>

    <!-- ================= 1. Was der Mensch korrigiert hat ================= -->
    <section class="mb-10 pb-10 border-b border-gray-200">
      <!-- Die Erklärung hängt am Info-Icon, nicht in einem Absatz darunter.
           Sie wird EINMAL gebraucht; als Textblock schöbe sie die Tabelle bei
           jedem Besuch nach unten. Dasselbe Muster wie im Kopf der Seite. -->
      <h3 class="flex items-center gap-1.5 text-lg font-semibold text-gray-800 mb-3">
        <span>Diagnose <span class="font-normal text-gray-500">— was Menschen korrigiert haben</span></span>
        <BaseInfoIcon :text="HILFE_DIAGNOSE" label="Diagnose" :oben="false" />
      </h3>

      <div v-if="diagnose" class="text-xs text-gray-600 mb-2">
        <!-- ACHTUNG, die Unterpower-Regel aus R4, eine Ebene höher: eine
             Kennzahl über eine Handvoll Zellen ist keine Kennzahl. Statt sie
             kleiner zu drucken, wird sie ERSETZT — sonst rechnet jemand mit
             ihr weiter. -->
        <span v-if="zuWenigMaterial" class="inline-flex items-center gap-1.5 text-amber-800 font-semibold">
          <span>
            Zu wenig Material: {{ diagnose.n_reference_cells }} korrigierte Zellen
            (unter {{ minReferenzZellen }})
          </span>
          <BaseInfoIcon :text="HILFE_ZU_WENIG" label="zu wenig Material" ton="amber" :oben="false" />
        </span>
        <span v-else>
          <strong>{{ diagnose.n_reference_cells }}</strong> korrigierte Zellen aus
          <strong>{{ diagnose.n_messages }}</strong> Nachrichten
          <span v-if="diagnose.set_name">· Vergleichssatz <code>{{ diagnose.set_name }}</code></span>
          <!-- ACHTUNG: hier stand "· Herkunft human" (diagnose.quellen). Der
               Wert ist aus dieser Oberfläche heraus NICHT änderbar — die
               Seite schickt den Parameter `quellen` gar nicht mit, und der
               Vorgabewert ist über drei Ebenen hinweg "human". Die Zeile
               konnte also nie etwas anderes sagen als "human" und war damit
               keine Auskunft, sondern Rauschen an einer Stelle, an der drei
               echte Zahlen um Aufmerksamkeit konkurrieren.
               Dazu kam eine Verwechslung: "Herkunft zeigen" am Vorschlag
               weiter unten meint etwas völlig anderes (den eingefrorenen
               Diagnose-Schnappschuss). Zwei Bedeutungen, ein Wort, eine Seite
               — der Knopf dort heißt seit dem 10.08. "Worauf das beruht". -->
          <span v-if="diagnose.filter && diagnose.filter.human_without_model">
            · {{ diagnose.filter.human_without_model }} ohne Modellwert ausgelassen
          </span>
        </span>
      </div>

      <div class="border border-gray-200 rounded-lg p-3 overflow-x-auto">
        <table class="w-full text-xs">
          <thead>
            <tr class="border-b border-gray-300 text-gray-600">
              <th class="text-left py-1 pr-4">Kriterium</th>
              <th class="text-left py-1 px-2">Modell → Mensch</th>
              <th class="text-right py-1 px-2">Zellen</th>
              <th class="text-left py-1 pl-2">Nachrichten</th>
            </tr>
          </thead>
          <tbody>
            <!-- Der Schlüssel steht auf dem <template>, NICHT auf den beiden
                 <tr>: eine Zeile und ihre aufgeklappten Beispiele sind ein
                 Eintrag, und Vue 3 verlangt den Schlüssel an der Wurzel der
                 Wiederholung. -->
            <template v-for="(c, i) in cluster" :key="c.criterion_key + '-' + i">
              <tr class="border-t border-gray-200 align-top">
                <td class="py-1 pr-4">
                  <!-- fixiert: die Tabelle liegt in einem overflow-x-auto, und
                       das beschneidet auch nach oben. Ohne den Schalter wäre
                       der Tooltip unsichtbar, ohne dass irgendwo ein Fehler
                       auftritt. -->
                  <!-- Seit dem 13.08. steht die Beschreibung des Kriteriums
                       mit im Tooltip — dieselbe, die auch in den Meta-Prompt
                       geht. Ohne sie gäbe es eine Auskunft, die das Modell
                       bekommt und der Mensch daneben nicht. -->
                  <BaseTooltip :text="kriteriumHilfe(c)" fixiert>
                    <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {{ c.criterion_name || c.criterion_key }}
                    </span>
                  </BaseTooltip>
                </td>
                <td class="py-1 px-2 font-mono">
                  {{ formatZahl(c.model_value) }} → {{ formatZahl(c.human_value) }}
                </td>
                <td class="text-right py-1 px-2 font-mono">{{ c.count }}</td>
                <td class="py-1 pl-2">
                  <!-- Die ANZAHL, die ids im Tooltip. Bis zum 10.08. stand hier
                       die Liste selbst: nackte Zahlen, nicht klickbar, ohne
                       Nachrichtentext daneben. Aus einer id allein lässt sich
                       nichts schließen — sie ist ein Nachschlagewert für den,
                       der ohnehin in die Datenbank sieht, und für den bleibt
                       sie im Tooltip erhalten. In der Fläche kostete sie eine
                       ganze Spaltenbreite für nichts.
                       fixiert: dieselbe Falle wie beim Kriterium daneben — die
                       Tabelle liegt in einem overflow-x-auto, und das
                       beschneidet auch nach oben. -->
                  <BaseTooltip :text="`Nachrichten: ${(c.message_ids || []).join(', ') || '—'}`" fixiert>
                    <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2 text-gray-500">
                      {{ (c.message_ids || []).length }}
                    </span>
                  </BaseTooltip>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- ACHTUNG, hier steht die Auskunft VOLLSTÄNDIG und nicht im
             Tooltip: das ist der Leerzustand. Wer ihn sieht, hat nichts
             anderes vor sich — die Rettung hinter einem 16-px-Icon zu
             verstecken, das neben einem grauen Satz steht, ist die
             schlechteste Kombination der Seite. -->
        <p v-if="!loading && cluster.length === 0 && unbekannterSatz" class="text-gray-500 text-xs py-2">
          Den Vergleichssatz <span class="font-mono">{{ setName }}</span> gibt es nicht — die
          Tabelle ist deshalb leer, nicht weil nichts korrigiert wurde.
          <template v-if="evalSets.length">
            Vorhanden sind:
            <span v-for="(s, i) in evalSets" :key="s.set_name">
              <span class="font-mono">{{ s.set_name }}</span
              ><span v-if="i < evalSets.length - 1">, </span>
            </span>.
          </template>
        </p>
        <p v-else-if="!loading && cluster.length === 0" class="text-gray-500 text-xs py-2">
          Keine korrigierten Zellen im Vergleichssatz
          <span class="font-mono">{{ setName || "—" }}</span>. Ohne Korrekturen entsteht keine
          Regel; möglicherweise wurde auch in einem anderen Vergleichssatz korrigiert.
        </p>
      </div>

      <!-- ACHTUNG: hier stand bis zum 10.08. der Aufklapper "Grenzen dieser
           Auswertung" — drei Sätze (ein Bewerter / Auswahleffekt / die
           dreifach überladene 0). Er ist AUS DER ANZEIGE genommen, nicht aus
           den Daten: `diagnose.grenzen` kommt weiter mit und wandert
           unverändert in `optimizer_proposals.diagnose`. Dort wird er
           gebraucht — ein Vorschlag, den jemand in einem halben Jahr liest,
           trägt seine Vorbehalte dann bei sich.
           Der Grund für die Streichung ist nicht Platz, sondern Adressat: die
           drei Sätze sagen einem Korrektor nichts, was er nicht ohnehin weiß
           (dass er selbst der eine Bewerter ist, und dass er korrigiert, was
           ihm auffällt). Ein Hinweis, der seinem Leser Selbstverständliches
           sagt, kostet ihn das Zutrauen in die Hinweise daneben. -->
    </section>

    <!-- ========================= 2. Der Optimierer ========================= -->
    <section class="mb-10 pb-10 border-b border-gray-200">
      <!-- "Der Optimierer" hiess dieser Abschnitt bis zum 09.08. (ein Akteur,
           kein Gegenstand), danach "Optimierer-Prompt" — und das war der
           GEGENSTAND, während der Abschnitt in Wahrheit eine HANDLUNG enthält.
           Wer hierher kommt, will einen Vorschlag; den Meta-Prompt fasst er
           selten bis nie an. Die Überschrift heißt jetzt wie der Knopf, der
           unten darunter steht. -->
      <h3 class="flex items-center gap-1.5 text-lg font-semibold text-gray-800 mb-3">
        <span>Vorschlag erzeugen</span>
        <BaseInfoIcon :text="HILFE_OPTIMIERER" label="Vorschlag erzeugen" :oben="false" />
      </h3>

      <!-- Bleibt ein sichtbarer Kasten und wandert NICHT ins Tooltip: er
           beschreibt keinen Hintergrund, sondern eine Sackgasse — ohne
           Optimierer-Prompt liefert "Vorschlag erzeugen" nichts. Kein Icon
           daneben: der Satz ist vollständig, und ein Tooltip, der ihn nur
           schwächer wiederholt, ist ein Versprechen ohne Inhalt. -->
      <div
          v-if="!optimizerActive && !loading"
          class="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-900 text-sm"
      >
        Kein Optimierer-Prompt gespeichert — erst hier einen Text speichern, dann
        Vorschläge erzeugen.
      </div>

      <!-- ⚠️ Der Meta-Prompt liegt seit dem 10.08. hinter einem Klick. Er ist
           Einrichtung, keine Alltagshandlung: einmal geschrieben, danach
           monatelang unverändert — und er belegte mit zehnzeiligem Textfeld,
           Notizfeld, Speicherknopf und Versionszeile den halben Abschnitt VOR
           dem Knopf, wegen dem man hier ist.

           ACHTUNG, `:open` hängt an `!optimizerActive`: gibt es noch keine
           Fassung, steht der Kasten offen. Der amber-Hinweis darüber schickt
           den Nutzer genau hierher — ihn hinter einen Aufklapper zu legen,
           hieße eine Sackgasse zu melden und den Ausweg zu verstecken.
           Bewusst NICHT zusätzlich an optimizerDirty gehängt: das Feld ändert
           sich beim Tippen, und ein Kasten, der sich beim nächsten Rendern von
           selbst wieder aufklappt, nimmt dem Nutzer die Entscheidung ab. -->
      <details class="mb-4 group" :open="!optimizerActive">
        <summary class="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none hover:text-gray-800 list-none [&::-webkit-details-marker]:hidden">
          <ChevronRightIcon class="w-3.5 h-3.5 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
          <!-- ⚠️ Hieß bis zum 13.08. "Anweisung an das Modell" — wortgleich
               mit dem <label> drei Zeilen weiter unten, das direkt darunter
               steht, sobald jemand aufklappt. Zwei identische Zeilen
               übereinander lesen sich wie zwei Dinge und sind eines.
               Weggefallen ist der Aufklapper-Titel, nicht das Label: das
               Label gehört per `for` zum <textarea> und ist damit die
               Ansage für Screenreader. -->
          <span class="underline">Optimierer-Prompt</span>
          <span v-if="optimizerActive" class="text-gray-400 no-underline">
            v{{ optimizerActive.version }}
          </span>
        </summary>

      <!-- ⚠️ Dieser Text ist der RAHMEN, und er gilt seit dem 16.08. für BEIDE
           Modi. Bis dahin galt er nur für „Anhängen“: „Überarbeiten“ fuhr eine
           zweite Vorlage, die als Konstante im Code stand und hier gar nicht
           auftauchte — wer den Text las oder änderte, las etwas anderes, als
           gesendet wurde.

           Was der Modus daraus macht, steht in der Instruktion darunter. Die
           bleibt im Code, und zwar aus einem Grund, den der Satz auch nennt:
           sie trägt das Antwortformat, gegen das drüben geparst wird. Format
           und Parser gehören in dieselbe Änderung. -->
      <!-- ⚠️ Hier stand bis zum 16.08. (abends) der Satz „Gilt für beide Modi
           … die Anweisung wird an der Stelle {instruktion} eingesetzt".
           Gestrichen, weil der Abschnitt zu voll wurde (Simon).

           Die Auskunft ist damit nicht weg, sie steht nur nicht mehr als
           Fließtext da: der Aufklapper unter dem Feld zeigt die Anweisung DES
           GEWÄHLTEN Modus und wechselt mit dem Umschalter — daran ist beides
           abzulesen, die Zweiteilung und die Zugehörigkeit. Und die Stelle
           selbst wird genannt, sobald sie WIRKLICH gebraucht wird: in der
           Warnung darunter, wenn sie im Rahmen fehlt. Ein Hinweis, der immer
           dasteht, wird nicht gelesen — dieselbe Regel wie bei
           promptWarnung. -->

      <!-- ⚠️ Das sichtbare <label> ist am 16.08. gefallen. Der Aufklapper
           direkt darüber heißt „Optimierer-Prompt“, der Platzhalter im Feld
           sagt, was hineingehört, und der Tooltip am Speichern-Knopf sagt,
           was daraus wird — drei Ansagen für ein Textfeld.

           GEBLIEBEN ist die Ansage für Screenreader, jetzt als `aria-label`:
           ohne sie wäre das Feld ein unbeschriftetes Eingabeelement, und der
           Aufklapper-Titel daneben ist KEIN Ersatz — er gehört zu keinem
           Bedienelement. Genau davor warnt
           test_no_heading_repeats_the_label_below_it; die Zusicherung ist
           nicht gefallen, sie hat die Form gewechselt. Dieselbe Bauweise wie
           am Notizfeld darunter, das nie ein sichtbares Label hatte.

           Der Abstand nach oben wandert mit: er saß am <label>, das es nicht
           mehr gibt. -->
      <textarea
          id="reflection-meta"
          v-model="optimizerContent"
          rows="10"
          aria-label="Anweisung an das Modell"
          class="w-full mt-3 px-3 py-2 border border-gray-300 rounded-md text-xs font-mono text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          placeholder="Zum Beispiel: Formuliere aus den häufigsten Abweichungen genau eine zusätzliche Regel …"
      ></textarea>

      <!-- ⚠️ Fehlt die Stelle im Rahmen, sagt die Seite es VOR dem Klick. Das
           Backend lehnt einen Überarbeiten-Lauf dann mit 409 ab (der Rahmen
           brächte dem Modell sonst den Anhängen-Auftrag), und „Anhängen“ läuft
           unverändert weiter — genau so steht es hier.

           Er hängt an `platzhalter`, also an der geladenen Auskunft: ohne sie
           wird nichts behauptet. Eine Warnung über einen Platzhalter, den
           niemand nachgesehen hat, wäre schlimmer als keine. -->
      <p
          v-if="rahmenOhneStelle"
          class="mt-2 text-sm text-amber-800 inline-flex items-start gap-1.5"
      >
        <ExclamationTriangleIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
        <span>
          Der Rahmen führt <code class="font-mono text-xs">{{ platzhalter }}</code> nicht.
          „Anhängen“ läuft weiter — dann muss die Anweisung im Text selbst stehen.
          „Überarbeiten“ wird abgelehnt.
        </span>
      </p>

      <!-- Die Instruktion des GEWÄHLTEN Modus, schreibgeschützt.
           ⚠️ Sie kommt über die Leitung und steht nicht hier im Frontend: sie
           ist drüben eine Konstante und wird dort auch eingesetzt. Eine Kopie
           an dieser Stelle wäre eine zweite Wahrheit über den Text, der
           wirklich gesendet wird, und liefe beim nächsten Deploy auseinander.

           Zugeklappt, weil sie sich nie ändert, ohne dass jemand ein Image
           baut — aber auffindbar, weil sie die Hälfte des Prompts ist. -->
      <details v-if="instruktionText" class="mt-2 group">
        <summary class="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none hover:text-gray-800 list-none [&::-webkit-details-marker]:hidden">
          <ChevronRightIcon class="w-3.5 h-3.5 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
          <span class="underline">Anweisung für „{{ modus === "revise" ? "Überarbeiten" : "Anhängen" }}“</span>
          <span class="text-gray-400 no-underline">— fest im Code</span>
        </summary>
        <pre class="mt-2 p-3 whitespace-pre-wrap break-words bg-gray-50 border border-gray-200 rounded-md text-xs font-mono text-gray-700">{{ instruktionText }}</pre>
        <p class="mt-1 text-xs text-gray-500">
          Regelprompt {{ instruktionHash }} · sie trägt das Antwortformat, gegen
          das geprüft wird — änderbar nur mit einem neuen Image.
        </p>
      </details>

      <div class="flex flex-col gap-2 mt-2 md:flex-row md:items-center md:justify-between">
        <div class="flex flex-wrap items-center gap-2">
          <input
              v-model="optimizerNote"
              type="text"
              placeholder="Notiz zu dieser Fassung (optional)"
              aria-label="Notiz zur neuen Optimierer-Version"
              class="px-3 py-2 border border-gray-300 rounded-md text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <!-- ACHTUNG, der Tooltip nennt seit dem 09.08. den Fall, der
               WIRKLICH eintreten kann. Vorher stand hier "ist er mit einer
               vorhandenen Version identisch, entsteht keine zweite Zeile" --
               das beschreibt den 409-Zweig gegen die AKTIVE Fassung, und den
               sperrt optimizerDirty ohnehin weg. Was tatsächlich passiert:
               put_optimizer_prompt reaktiviert eine bekannte INAKTIVE Zeile
               (reflection.py:347-355), die Versionsnummer springt dabei
               zurück, und payload.note wird in diesem Zweig nie verwendet. -->
          <BaseTooltip
              text="Legt den Text als neue Version an und aktiviert sie; die vorherige bleibt erhalten. War genau dieser Text schon einmal eine Version, wird stattdessen jene wieder aktiv — die Nummer springt dann zurück und die Notiz bleibt ungenutzt."
          >
            <BaseButton
                variant="primary"
                :disabled="savingOptimizer || !optimizerDirty"
                @click="saveOptimizer"
            >
              {{ savingOptimizer ? "Speichere…" : "Optimierer-Prompt speichern" }}
            </BaseButton>
          </BaseTooltip>
        </div>

        <!-- ⚠️ Die Notiz stand hier bis zum 16.08. UNGEKÜRZT. Die aktive
             Fassung führt zwei Befunde samt Messwert („B3+B6 (13.08.): 'ENDS
             WITH' -> 'CONTAINS' …", 190 Zeichen); sie schob Versionsnummer,
             Datum und Anzahl aus dem Blick und lief in der schmalen Spalte
             über drei Zeilen.

             Gekürzt wird in der ANZEIGE, nicht in der Datenbank: die Notiz
             ist die Begründung der Fassung und gehört vollständig erhalten.
             Der ganze Text steht im Tooltip — und der erscheint NUR, wenn
             wirklich gekürzt wurde, sonst verspräche er einen Rest, den es
             nicht gibt.

             Gerechnet wird einmal, in `optimizerNotiz`: die Frage „ist
             gekürzt?" und der gekürzte Text kommen aus derselben Rechnung.
             Zwei getrennte Rechnungen liefen hier zuverlässig auseinander. -->
        <div class="text-xs text-gray-500">
          <span v-if="optimizerActive">
            aktiv: v{{ optimizerActive.version }} · {{ formatTs(optimizerActive.created_at) }}
          </span>
          <span v-else>keine aktive Version</span>
          <span v-if="optimizerVersions.length">
            · {{ optimizerVersions.length }}
            {{ optimizerVersions.length === 1 ? "Fassung" : "Fassungen" }}
          </span>
          <template v-if="optimizerNotiz.kurz">
            ·
            <BaseTooltip v-if="optimizerNotiz.gekuerzt" :text="optimizerNotiz.voll">
              <span class="cursor-help underline decoration-dotted">{{ optimizerNotiz.kurz }}</span>
            </BaseTooltip>
            <span v-else>{{ optimizerNotiz.kurz }}</span>
          </template>
        </div>
      </div>
      </details>

      <!-- Der Lauf selbst — jetzt das Erste im Abschnitt, nicht mehr das
           Letzte. Bewusst hier und nicht bei den Vorschlägen: er benutzt genau
           diesen Prompt und den Vergleichssatz aus dem Kopf der Seite.
           Die eigene Überschrift "Lauf starten" ist weggefallen: der Abschnitt
           heißt seit dem 10.08. selbst nach dieser Handlung, und eine
           Unterüberschrift, die die Überschrift darüber wiederholt, ist eine
           Gliederungsebene ohne Inhalt. -->
      <div>

        <!-- ⚠️ WELCHER Prompt hier fortgeschrieben wird. Bis zum 09.08. stand
             es nirgends, und die Antwort war fest verdrahtet: immer
             rag_prompt_template, unabhängig vom Bewertungsmodus. Im Modus LLM
             entstand die Regel damit aus Korrekturen an LLM-Bewertungen und
             landete am RAG-Prompt — der Vergleich lief sauber durch, die
             Version wurde aktiv, und bewertet wurde weiter wie zuvor.
             Jetzt folgt der Schlüssel dem Modus, und er steht hier. -->
        <p v-if="wirksam" class="mb-2 text-sm flex flex-wrap items-center gap-1.5">
          <template v-if="wirksamerSchluessel">
            <!-- ⚠️ Der Zielsatz nennt die gewählte HANDLUNG, und das ist der
                 Grund für diese Stelle: der Umschalter darunter ist ein
                 verborgener Zustand, und ein verborgener Zustand, der den
                 Prompt verändert, gehört dorthin, wo ohnehin steht, worauf
                 sich der Lauf auswirkt. Man kann die Bedienelemente nicht
                 ansehen, ohne die scharfgeschaltete Handlung zu lesen. -->
            <span class="text-gray-600">{{
                modus === "revise"
                  ? "Eine Regel wird überarbeitet in"
                  : "Die Regel wird angehängt an"
              }}</span>
            <!-- Schlüssel UND Versionsnummer in einer Pille: sie bezeichnen
                 zusammen genau einen Text, und die Nummer stand vorher hinter
                 zwei Trennpunkten, als wäre sie eine dritte Kennzahl neben
                 Regeln und Zeichen.

                 ⚠️ Der Modus ist hier weggefallen ("(Modus RAG)"). Er war
                 keine zweite Auskunft: rag_prompt_template steht direkt
                 daneben und sagt dasselbe. Wo er wirklich etwas sagt, steht
                 er weiterhin — im ML-Zweig darunter, wo es gar keinen Prompt
                 gibt, und in HILFE_WIRKSAM, das den Zusammenhang erklärt. -->
            <code class="px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs">{{
                wirksamerSchluessel
              }}<template v-if="wirksam.active_version"> v{{ wirksam.active_version }}</template></code>
            <!-- ⚠️ Hier stand vom 13.08. bis zum 16.08. (abends) „— der aktive
                 Bewertungsprompt, und die Basis auch des nächsten
                 Vorschlags". Gestrichen, weil der Kopf zu voll wurde (Simon).

                 Der Sachverhalt ist kontraintuitiv und im Bestand belegt:
                 „Als Entwurf übernehmen" legt die Zeile mit gate_status
                 'draft' und is_active=false an, erzeuge_vorschlag liest aber
                 active_prompt_version() — zwei Übernahmen nacheinander
                 zweigen deshalb beide von derselben aktiven Version ab.

                 WO DIE AUSKUNFT GEBLIEBEN IST: in der Meldung nach dem
                 Übernehmen (adoptConfirmed). Das ist der stärkere der beiden
                 Orte — sie erscheint genau in dem Moment, in dem die falsche
                 Erwartung entsteht, statt vorbeugend im Kopf, wo sie
                 überflogen wird. Gefallen ist also die Vorbeugung, nicht die
                 Auskunft.

                 NICHT nach HILFE_WIRKSAM geschoben: der Text ist wortgleich
                 mit dem der Versionsseite (zwei Formulierungen läsen sich wie
                 zwei Sachverhalte) und läge mit dem Zusatz über der
                 Tooltip-Grenze von 260 Zeichen. Beides ist beim Versuch
                 aufgefallen, nicht beim Nachdenken.

                 Die amber Pille „gegen v3 formuliert · aktiv ist v4" am
                 Vorschlag trägt es NICHT: sie vergleicht gegen die aktive
                 Version und erscheint nach einer Übernahme gar nicht. -->
            <BaseInfoIcon :text="HILFE_WIRKSAM" label="welcher Prompt" :oben="false" />
            <!-- Die Maße dieses Prompts. Gezählt wird in promptMass.js —
                 derselben Stelle, aus der auch die Versionsseite ihre Zahlen
                 nimmt. -->
            <template v-if="aktiverPromptText">
              <span class="text-gray-400" aria-hidden="true">·</span>
              <BaseTooltip :text="HILFE_REGELN">
                <span :class="regelnHoch ? 'text-amber-800 font-semibold' : 'text-gray-600'">
                  {{ regelAnzahl(aktiverPromptText) }} Regeln
                </span>
              </BaseTooltip>
              <span class="text-gray-400" aria-hidden="true">·</span>
              <BaseTooltip :text="HILFE_ZEICHEN">
                <span :class="zeichenHoch ? 'text-amber-800 font-semibold' : 'text-gray-600'">
                  {{ promptZeichen(aktiverPromptText) }} Zeichen
                </span>
              </BaseTooltip>
            </template>
          </template>
          <template v-else>
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0 text-amber-600" aria-hidden="true" />
            <span class="text-amber-800 font-semibold">
              Modus {{ wirksam.rating_mode }} — es wird ohne Prompt bewertet
            </span>
            <BaseInfoIcon :text="HILFE_MODUS_ML" label="Modus ML" ton="amber" :oben="false" />
          </template>
        </p>

        <!-- ⚠️ Die Warnung erscheint NUR, wenn sie etwas sagt. Auf dem heutigen
             Bestand (1135 Zeichen, 11 Regeln) steht hier nichts — ein Hinweis,
             der immer erscheint, wird nicht gelesen. Sie ist GERECHNET und
             kein fester Text. -->
        <p
            v-if="promptWarnungText"
            class="mb-2 text-sm text-amber-800 inline-flex items-start gap-1.5"
        >
          <ExclamationTriangleIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
          <span>{{ promptWarnungText }}</span>
        </p>

        <!-- ⚠️ Ein Umschalter, KEIN zweiter Knopf. „Vorschlag erzeugen“ ist
             Abschnittsname, Tooltip, Leerzustand und an drei Formtests
             gebunden; zwei Knöpfe zwängen zur Umbenennung. Und es sind auch
             nicht zwei Handlungen, sondern eine mit einem Parameter. -->
        <!-- ⚠️ Die sichtbare Beschriftung „Was der Optimierer tun soll" ist am
             16.08. (abends) gefallen — der Abschnitt wurde zu voll (Simon).
             Die beiden Wahlmöglichkeiten heißen „Anhängen" und „Überarbeiten"
             und tragen je einen Erklärtext; die Überschrift darüber sagte
             nichts, was der Zielsatz oben nicht schon sagt.

             GEBLIEBEN ist die Ansage für Screenreader, jetzt als
             `role="radiogroup"` mit `aria-label`: ohne sie wären es zwei
             lose Optionsfelder ohne Zusammenhang, und die Vorlesereihenfolge
             ließe offen, wozu sie gehören. Dieselbe Form wie am Textfeld
             darüber — die Zusicherung ist nicht gefallen, sie hat den Ort
             gewechselt. -->
        <div
            class="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5"
            role="radiogroup"
            aria-label="Was der Optimierer tun soll"
        >
          <label class="inline-flex items-center gap-1.5 text-sm cursor-pointer">
            <input v-model="modus" type="radio" value="append" class="accent-indigo-600" />
            <span>Anhängen</span>
            <BaseInfoIcon :text="HILFE_MODUS_APPEND" label="Anhängen" :oben="false" />
          </label>
          <label
              class="inline-flex items-center gap-1.5 text-sm"
              :class="reviseMoeglich ? 'cursor-pointer' : 'text-gray-400'"
          >
            <input
                v-model="modus"
                type="radio"
                value="revise"
                :disabled="!reviseMoeglich"
                class="accent-indigo-600"
            />
            <span>Überarbeiten</span>
            <BaseInfoIcon :text="HILFE_MODUS_REVISE" label="Überarbeiten" :oben="false" />
          </label>
          <!-- ⚠️ Der Grund steht als sichtbarer TEXT daneben, nicht in einem
               Tooltip: an einem `disabled`-Bedienelement erscheint in Chrome
               und Firefox keiner — also ausgerechnet dann nicht, wenn jemand
               ihn braucht. -->
          <span v-if="!reviseMoeglich" class="text-xs text-amber-800">
            Überarbeiten ist in diesem Stand abgeschaltet (OPTIMIZER_REVISE=0).
            Der Meta-Prompt ist damit byte-identisch zum Stand vor dem 11.08. —
            genau das ist der Zweck des Schalters.
          </span>
        </div>

        <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div class="flex flex-wrap items-center gap-2">
            <label class="text-sm text-gray-600" for="reflection-modell">Modell</label>

            <!-- ⚠️ Nur noch die Auswahl. Bis zum 19.08. stand ein
                 Freitextfeld daneben, damit ein Modell eingetragen werden
                 konnte, das drueben noch nicht liegt — der Rating-Dienst zieht
                 es beim Lauf nach (client.pull). Das ist auf Entscheidung
                 Simons gestrichen: zwei Bedienelemente fuer eine Zahl, und der
                 haeufige Fall ist die Auswahl. Der Preis steht hier, damit ihn
                 die naechste Sitzung nicht neu entdeckt — ein Modell, das noch
                 nicht vorliegt, ist von dieser Seite aus nicht mehr zu waehlen;
                 es muss vorher gezogen werden (ollama pull), danach steht es in
                 der Liste.

                 ⚠️ Der aktuelle Name bekommt IMMER einen Eintrag, auch wenn
                 er nicht in der Liste steht (Vorgabe des Backends, Modell
                 zwischenzeitlich entfernt, Liste nicht erreichbar). Ohne den
                 saesse die Auswahl sichtbar auf dem ersten Eintrag, waehrend
                 `model` etwas anderes traegt — die Seite zeigte den einen Namen
                 und schickte den anderen. Genau die Falle, gegen die das
                 Freitextfeld frueher als einzige Wahrheit gebaut war. -->
            <BaseTooltip :text="modellHilfe">
              <select
                  id="reflection-modell"
                  v-model="model"
                  aria-label="Modell auswählen"
                  class="px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                <!-- Der Platzhalter erscheint nur, solange gar nichts gewaehlt
                     ist. Er ist NICHT waehlbar: ein leerer Modellname schickte
                     den Lauf ins Leere, und `messenGesperrt` faengt ihn zwar
                     ab, der Optimierer-Knopf aber nicht. -->
                <option v-if="!model" value="" disabled>— kein Modell —</option>
                <option v-if="model && !modelInListe" :value="model">
                  {{ model }} · nicht in der Auswahl
                </option>
                <option v-for="m in waehlbareModelle" :key="m.name" :value="m.name">
                  {{ m.name }}{{ m.denkt ? ' · denkt' : '' }}
                </option>
              </select>
            </BaseTooltip>

            <!-- Der Satz erscheint nur im Zweifelsfall. Ein Hinweis, der
                 dauerhaft dasteht, wird nicht gelesen — dieselbe Regel, aus
                 der am 19.08. die Belegwarnung darunter gefallen ist. -->
            <span v-if="modellWarnung" class="text-xs text-amber-800">
              ⚠️ {{ modellWarnung }}
            </span>

            <label class="text-sm text-gray-600 ml-2" for="reflection-belegbudget">
              Komplette Nachrichten im Prompt
            </label>
            <!-- Der Tooltip trägt seit dem 18.08. auch die Standardzahl: der
                 Erklärtext unter dem Feld sagt zum leeren Feld nichts mehr.
                 Er ist GEBUNDEN und keine Konstante, weil die Zahl aus der
                 Antwort des Backends kommt (belegmasse.max_belege) — eine
                 „drei“ im Text wäre eine zweite Wahrheit neben
                 OPTIMIZER_MAX_BELEGE. -->
            <BaseInfoIcon :text="belegbudgetHilfe" label="Komplette Nachrichten im Prompt" ton="grau" :oben="false" />
            <!-- ⚠️ max kommt über die Leitung, nicht als Literal: gekürzt wird
                 drüben nach OPTIMIZER_BELEGBUDGET_MAX, und eine 24 in dieser
                 Datei wäre eine zweite Wahrheit, die beim ersten anderen Wert
                 auseinanderliefe — still, weil ein max-Attribut niemand
                 nachrechnet. Fehlt die Angabe, bleibt das Feld ohne Obergrenze
                 und das Schema fängt es ab.

                 ⚠️ Der Startwert steht ebenfalls nicht hier: reload() belegt
                 das Feld mit belegmasse.max_belege vor. Der Platzhalter bleibt
                 stehen, weil das Feld leerbar BLEIBT — leer ist weiterhin ein
                 eigener Zustand (Belege aus dem größten Befund) und nicht
                 dasselbe wie die vorbelegte Zahl. -->
            <input
                id="reflection-belegbudget"
                v-model="belegbudget"
                type="number"
                min="0"
                :max="belegbudgetMax"
                placeholder="(leer)"
                class="px-3 py-2 border border-gray-300 rounded-md text-sm w-24 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />

            <!-- ⚠️ Die DAUER steht seit dem 13.08. nur noch im Ladehinweis
                 unter diesem Knopf, nicht mehr zusätzlich hier. Sie stand an
                 beiden Stellen und mit verschiedenen Grenzen ("meist unter
                 zwei Minuten" gegen "…nach spätestens fünf bricht der Aufruf
                 ab") — zwei Zusagen für denselben Lauf.

                 Der Tooltip ist ohnehin der falsche Ort dafür: er erscheint
                 nur, wenn jemand die Maus hierher bewegt, und genau das tut
                 niemand, während er auf eine Antwort wartet. Der Ladehinweis
                 erscheint von selbst. Derselbe Grund steht drüben in
                 test_the_wait_is_visible_and_says_how_long. -->
            <BaseTooltip
                text="Schickt Diagnose und Optimierer-Prompt an das Modell und wartet auf die Regel. Der Aufruf läuft synchron. An den Bewertungen ändert sich nichts."
            >
              <!-- ⚠️ Im Modus ML gesperrt, und der GRUND steht als Text
                   darüber, nicht in diesem Tooltip: an einem `disabled`-Knopf
                   erscheint in Chrome und Firefox nie ein Tooltip — also
                   ausgerechnet dann nicht, wenn jemand ihn braucht. Das
                   Backend lehnt den Lauf ohnehin mit 409 ab; ihn hier gar
                   nicht erst zu starten spart einen Fehlversuch, der wie eine
                   Panne aussieht. -->
              <BaseButton
                  :variant="proposing || keinPrompt ? 'disabled' : 'primary'"
                  :disabled="proposing || keinPrompt"
                  @click="propose"
              >
                {{ proposing ? "Erzeuge…" : "Vorschlag erzeugen" }}
              </BaseButton>
            </BaseTooltip>
          </div>

          <!-- Sichtbarer Ladezustand. Ohne ihn ist die Seite von einer
               hängenden nicht zu unterscheiden. Der Knopf trägt den Satz
               NICHT ein zweites Mal — beide erscheinen gleichzeitig.

               Hier steht die Dauer, und zwar als EINZIGE Stelle: der Tooltip
               am Knopf darüber hat sie am 13.08. abgegeben. Der Kaltstart ist
               mitgewandert — er ist genau die Erklärung, die jemand braucht,
               während er wartet und die zwei Minuten schon vorbei sind. Das
               Backend wartet bis PROPOSE_TIMEOUT, Vorgabe 300 s, ausdrücklich
               deswegen (reflection.py:40-51). -->
          <p v-if="proposing" class="text-sm text-amber-700 inline-flex items-center gap-2">
            <span class="w-1.5 h-1.5 shrink-0 rounded-full bg-amber-500 animate-pulse" aria-hidden="true"></span>
            Der Optimierer denkt nach — meist unter zwei Minuten, nach einem Kaltstart des
            Modells länger; nach spätestens fünf bricht der Aufruf ab.
          </p>
        </div>

        <!-- ⚠️ Hier stand bis zum 18.08. ein Erklaerabsatz unter dem Feld, in
             drei Zustaenden. Er ist ERSATZLOS gestrichen (Entscheidung Simon,
             zweimal: erst der Zweig zum leeren Feld, dann der ganze Absatz).
             Der Grund ist derselbe wie beim Warnsatz vom 18.08. frueher am
             Tag: eine Auskunft, die dauerhaft dasteht, wird nicht gelesen und
             kostet nur Flaeche. Weniger Text, mehr Uebersicht.

             Was der Absatz sagte, sagt jetzt der Tooltip am Feld — mitsamt
             dem Vorbehalt zur Sortierung, der sonst ersatzlos verschwunden
             waere: die Diagnosetabelle oben wird OHNE Budget geladen.

             ⚠️ Am 19.08. ist auch die WARNUNG gestrichen (Entscheidung
             Simon) — der Satz „rund N Zeichen allein fuer diese Nachrichten,
             das Fenster steht auf ... Token, und Ollama schneidet einen zu
             langen Prompt ab, ohne es zu melden“. Mit ihm faellt die ganze
             Schaetzkette in belegMasse.js: sie rechnete Zeichen und behauptete
             damit etwas ueber Token, und wie schlecht diese Umrechnung traegt,
             ist am 18.08. gemessen (2,80 bis 4,76 Zeichen je Token, Faktor
             1,70). Was an dieser Stelle wirklich misst, steht darunter: die
             Tokenmessung gegen das Modell. -->

        <!-- Die Tokenmessung. Sie stand bis zum 19.08. UNTER der
             Belegwarnung, weil die eine in Zeichen schätzte und die andere in
             Token misst — zwei Auskünfte mit verschiedenem Preis, die nicht
             ineinander umgerechnet werden dürfen. Die Schätzung ist weg;
             übrig ist die Messung, und sie ist die einzige Auskunft zur
             Größe des Meta-Prompts.

             ⚠️ Auf Knopfdruck und nicht laufend — die Messung kostet einen
             echten Modellaufruf. Und die Zahl VERSCHWINDET, sobald jemand
             Modell, Modus, Budget oder Satz ändert (siehe watch). -->
        <div class="mt-2 flex flex-wrap items-center gap-2">
          <BaseTooltip :text="HILFE_MESSEN">
            <BaseButton
                variant="neutral"
                size="sm"
                :disabled="messenGesperrt"
                @click="messeTokens"
            >
              {{ messungLaeuft ? "misst…" : "Tokenzahl messen" }}
            </BaseButton>
          </BaseTooltip>

          <!-- Der Grund als TEXT und nicht im Tooltip: an einem gesperrten
               Knopf erscheint in Chrome und Firefox nie einer — also genau
               dann nicht, wenn jemand ihn braucht. -->
          <span v-if="messenGesperrtGrund" class="text-xs text-gray-500">
            {{ messenGesperrtGrund }}
          </span>

          <span v-if="messSatz" class="text-xs text-gray-700 font-mono">
            {{ messSatz }}
          </span>
          <!-- Der Vorbehalt hängt seit dem 19.08. AN der Zahl und steht nicht
               mehr als Absatz darunter. Grund: seit OPTIMIZER_NUM_CTX
               zurückgestellt ist, trifft er bei JEDEM Lauf zu — als Dauertext
               wäre er dieselbe ungelesene Zeile wie die Belegwarnung, die am
               selben Tag gefallen ist. Muster: die Cutoff-Auskunft vom 18.08.
               ⚠️ Die WARNUNG darunter bleibt ein Absatz. Sie erscheint nur,
               wenn wirklich gekappt wurde, und ein Befund, den man erst
               aufklappen muss, ist keiner. -->
          <BaseInfoIcon
              v-if="messVorbehalt"
              :text="messVorbehalt"
              label="Vorbehalt zur Tokenmessung"
              ton="grau"
              :oben="false"
          />
        </div>

        <p v-if="messWarnung" class="mt-1 text-xs text-amber-800">
          ⚠️ {{ messWarnung }}
        </p>
        <p v-if="messungFehler" class="mt-1 text-xs text-red-700">
          {{ messungFehler }}
        </p>
      </div>
    </section>

    <!-- ========================= 3. Die Vorschläge ========================= -->
    <section class="mb-10 pb-10 border-b border-gray-200">
      <h3 class="flex items-center gap-1.5 text-lg font-semibold text-gray-800 mb-3">
        <!-- "— offen" hiess der Zusatz im ersten Anlauf, und er war ungenau:
             gefiltert wird auf NICHT ÜBERNOMMEN, und ein verworfener
             Vorschlag steht hier weiter (er soll ja nachvollziehbar bleiben).
             Der Zusatz muss das Filterkriterium nennen, nicht ein ähnliches. -->
        <span>Regelvorschläge <span class="font-normal text-gray-500">— noch nicht übernommen</span></span>
        <BaseInfoIcon :text="HILFE_VORSCHLAEGE" label="Regelvorschläge" :oben="false" />
      </h3>

      <!-- ACHTUNG, offeneProposals statt proposals: adoptedProposals im
           Abschnitt darunter ist eine TEILMENGE derselben Liste. Ungefiltert
           stand jeder übernommene Vorschlag zweimal auf demselben Schirm,
           beide Male mit dem vollen Regeltext — und der letzte Abschnitt sah
           aus wie eine Wiederholung des vorletzten. -->
      <!-- ⚠️ `sichtbareProposals` statt `offeneProposals`: gezeigt werden die
           VORSCHLAEGE_SICHTBAR jüngsten, alles ältere steht hinter dem
           Ausklapper unter der Liste. Begrenzt ist NUR die Anzeige — geladen
           wird weiterhin der ganze Bestand. Ohne ihn gäbe es die Zahl im
           Ausklapper nicht, und der Leerzustand darunter behauptete „kein
           Vorschlag offen", sobald der sechste kommt.

           ACHTUNG, die Reihenfolge ist Voraussetzung und nicht Zufall:
           list_proposals sortiert nach id ABSTEIGEND. Die ersten fünf sind
           deshalb die jüngsten — sortierte der Endpunkt anders, schnitte
           dieser Ausschnitt die falschen weg. -->
      <ul class="divide-y divide-gray-200">
        <li v-for="p in sichtbareProposals" :key="p.id" class="py-4">
          <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <!-- ⚠️ „neu“ tritt zurück, sobald eine Art-Pille danebensteht.
                     Nebeneinander gelesen ergaben die beiden einen Satz, der
                     keiner ist: „neu ersetzt Regel 5“. Und „neu“ sagt hier
                     ohnehin am wenigsten — es ist die ABWESENHEIT einer
                     Entscheidung, während „ersetzt Regel 5“ sagt, was der
                     Vorschlag vorhat.
                     ACHTUNG, nur für „neu“: „verworfen“ bleibt stehen, auch
                     neben einer Art-Pille. Dort ist der Status die
                     Nachricht, und ohne ihn sähe ein verworfener Vorschlag
                     aus wie ein offener. -->
                <span
                    v-if="p.status !== 'neu' || !artText(p)"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    :class="statusClass(p.status)"
                >
                  {{ statusText(p.status) }}
                </span>
                <!-- ⚠️ WAS der Vorschlag mit dem Prompt vorhat — eigene
                     Abbildung, NICHT in STATUS_TEXT. Dort steht der Status
                     („neu“, „übernommen“, „verworfen“); zwei Bedeutungen in
                     einer Tabelle sind eine Bedeutung weniger. „Format
                     verfehlt“ ist rot, weil es der eine Fall ist, in dem der
                     Text darunter KEINE Regel ist, sondern die rohe Antwort
                     des Modells. -->
                <span
                    v-if="artText(p)"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    :class="artClass(p)"
                >
                  {{ artText(p) }}
                </span>
                <span class="text-xs text-gray-500 font-mono">{{ p.model }}</span>
                <!-- ⚠️ Ein Revise-Vorschlag hat KEINE Optimierer-Version: sein
                     Meta-Prompt ist eine Code-Konstante, weil
                     optimizer_prompt_versions kein Feld für die Art des Prompts
                     hat und nur eine aktive Zeile zulässt. Ohne die
                     Fallunterscheidung stünde hier „Optimierer v“ ohne Nummer
                     — genau der Anzeigefehler vom 09.08. Die Provenienz hängt
                     dann am Hash des Textes. -->
                <span v-if="p.optimizer_version" class="text-xs text-gray-500">
                  Optimierer v{{ p.optimizer_version }}
                </span>
                <BaseTooltip v-else-if="regelpromptHash(p)" :text="HILFE_REGELPROMPT" fixiert>
                  <span class="text-xs text-gray-500 cursor-help">
                    Regelprompt {{ regelpromptHash(p) }}
                  </span>
                </BaseTooltip>
                <span class="text-xs text-gray-500">{{ formatTs(p.created_at) }}</span>
                <span v-if="p.set_name" class="text-xs text-gray-500">
                  Vergleichssatz <code>{{ p.set_name }}</code>
                </span>

                <!-- ⚠️ Nur wenn er ABWEICHT. Den Schlüssel an jeden Vorschlag
                     zu schreiben wäre bei einem Bestand, in dem alle zum
                     wirksamen gehören, eine Angabe ohne Aussage — und eine
                     Angabe, die immer dasteht, wird nicht gelesen. Weicht er
                     ab, ist es die wichtigste Angabe der Zeile: die daraus
                     entstehende Version wirkt im heutigen Modus nicht. -->
                <BaseTooltip v-if="andererSchluessel(p)" :text="HILFE_ANDERER_SCHLUESSEL" fixiert>
                  <span
                      class="inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 cursor-help"
                  >
                    {{ p.prompt_key }}
                    <span class="font-semibold">wirkt nicht</span>
                  </span>
                </BaseTooltip>

                <!-- ⚠️ Gegen WELCHE Fassung formuliert wurde, wenn das nicht
                     mehr die aktive ist. Das SPERRT nicht — gesperrt wird nur,
                     wenn die Handlung selbst nicht mehr passt (Zielregel weg
                     oder geändert). Bei dem Tempo hier (v1 bis v12 in fünf
                     Tagen) entwertete die strengere Regel praktisch jede
                     Aktivierung sämtliche offenen Vorschläge. Der Hinweis
                     gehört trotzdem in die Kopfzeile: er ist beim Überfliegen
                     die wichtigste Einschränkung der Zeile. -->
                <BaseTooltip v-if="andereBasis(p)" :text="HILFE_ANDERE_BASIS" fixiert>
                  <span
                      class="inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 cursor-help"
                  >
                    gegen v{{ p.diagnose.basis_prompt_version }} formuliert · aktiv ist
                    v{{ wirksam.active_version }}
                  </span>
                </BaseTooltip>
              </div>

              <p class="mt-2 text-sm text-gray-800 whitespace-pre-line break-words">{{ p.suggestion }}</p>

              <!-- ⚠️ Alt und neu NEBENEINANDER, und ausdrücklich nicht im
                   Aufklapper „Worauf das beruht“: der alte Regeltext ist das,
                   was man lesen MUSS, um das Urteil zu fällen — er ist keine
                   Hintergrundinformation.

                   ⚠️ Kein `overflow-x-auto` an diesem Block. Wäre er ein
                   Scroll-Container, verlangte
                   test_every_tooltip_in_a_scroll_box_escapes_it an jedem
                   Tooltip darin `fixiert` — eine Bindung, die man sich für
                   nichts einhandelt. `break-words` tut es hier.

                   ⚠️ `border-l-<farbe>`, nicht `border-<farbe>`: bei gleicher
                   Spezifität entscheidet die Tailwind-Palettenreihenfolge, und
                   ein `border-red-300` neben einem `border-gray-200` ist ein
                   Glücksspiel, das nur im Browser sichtbar wird. -->
              <div v-if="p.ziel_regel_alt" class="mt-2 grid gap-2 md:grid-cols-2">
                <div class="border-l-2 border-l-gray-300 pl-2">
                  <p class="text-xs text-gray-500">Regel {{ p.ziel_regel }} heute</p>
                  <p class="text-sm text-gray-600 whitespace-pre-line break-words">{{ p.ziel_regel_alt }}</p>
                </div>
                <div class="border-l-2 border-l-indigo-300 pl-2">
                  <p class="text-xs text-gray-500">
                    {{ p.vorschlag_art === "delete" ? "Danach" : `Regel ${p.ziel_regel} neu` }}
                  </p>
                  <p
                      v-if="p.vorschlag_art === 'delete'"
                      class="text-sm text-gray-600 italic"
                  >
                    wird gestrichen — die Nummerierung der übrigen Regeln bleibt,
                    die Lücke ist gewollt
                  </p>
                  <p v-else class="text-sm text-gray-800 whitespace-pre-line break-words">{{ p.suggestion }}</p>
                </div>
              </div>

              <!-- ⚠️ Die Vorschau zeigt den GANZEN Prompt, und das ist der
                   Punkt: der Alt/Neu-Block darüber sagt, was sich ändert, aber
                   nicht, wogegen es danach steht. Vorschlag 13 des Bestands
                   ersetzt Regel 5 durch einen Satz, der der unveränderten
                   Regel 6 widerspricht — im Alt/Neu-Block kommt Regel 6 gar
                   nicht vor.

                   ⚠️ Kein `overflow-x-auto`, aus demselben Grund wie oben.
                   `break-words` trägt auch hier.

                   ⚠️ `border-l-<farbe>` an der Marke: `border-indigo-300`
                   färbte alle vier Seiten und verlöre bei gleicher
                   Spezifität gegen die Palettenreihenfolge. -->
              <button
                  v-if="p.vorschau_text"
                  type="button"
                  class="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
                  :aria-expanded="expandedVorschau[p.id] ? 'true' : 'false'"
                  @click="toggleVorschau(p.id)"
              >
                <ChevronRightIcon
                    class="w-3.5 h-3.5 transition-transform"
                    :class="expandedVorschau[p.id] ? 'rotate-90' : ''"
                />
                <span class="underline">
                  {{ expandedVorschau[p.id] ? "Vorschau schließen" : "Der Prompt danach" }}
                </span>
              </button>
              <BaseInfoIcon
                  v-if="p.vorschau_text"
                  :text="HILFE_VORSCHAU"
                  label="Vorschau"
                  :oben="false"
                  wrapperClass="ml-1 align-middle"
              />

              <div
                  v-if="p.vorschau_text && expandedVorschau[p.id]"
                  class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3"
              >
                <p v-if="p.vorschlag_art === 'delete'" class="text-xs text-gray-600 mb-2">
                  Keine Zeile ist neu — eine fehlt. Die gestrichene Regel
                  {{ p.ziel_regel }} steht oben links.
                </p>
                <p
                    v-for="(zeile, i) in vorschauZeilen(p)"
                    :key="i"
                    class="text-xs font-sans leading-relaxed whitespace-pre-wrap break-words border-l-2 pl-2"
                    :class="zeile.neu
                      ? 'border-l-indigo-300 bg-indigo-50 text-gray-900'
                      : 'border-l-transparent text-gray-600'"
                >{{ zeile.text || " " }}</p>
              </div>

              <button
                  type="button"
                  class="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
                  :aria-expanded="expandedProposal[p.id] ? 'true' : 'false'"
                  @click="toggleProposal(p.id)"
              >
                <ChevronRightIcon
                    class="w-3.5 h-3.5 transition-transform"
                    :class="expandedProposal[p.id] ? 'rotate-90' : ''"
                />
                <!-- NICHT "Herkunft": das Wort stand oben in der Diagnosezeile
                     schon für etwas völlig anderes (die score_source-Filterung).
                     Zwei Bedeutungen auf einer Seite sind eine Bedeutung
                     weniger. Hier geht es um die Zahlen, aus denen die Regel
                     entstanden ist — also darum, worauf sie beruht. -->
                <span class="underline">
                  {{ expandedProposal[p.id] ? "Grundlage verbergen" : "Worauf das beruht" }}
                </span>
              </button>

              <!-- Die Herkunft wandert mit dem Vorschlag mit: die Diagnose oben
                   zeigt den HEUTIGEN Bestand, dieser Block den Stand, aus dem
                   die Regel entstanden ist. Beides zu verwechseln hiesse, eine
                   Regel gegen Zahlen zu lesen, die sie nie gesehen hat. -->
              <div v-if="expandedProposal[p.id]" class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div class="text-xs text-gray-600 mb-2">
                  Stand bei der Erzeugung:
                  {{ (p.diagnose && p.diagnose.n_reference_cells) || 0 }} korrigierte Zellen aus
                  {{ (p.diagnose && p.diagnose.n_messages) || 0 }} Nachrichten
                  <!-- ⚠️ Hier stand bis zum 24.08. „· N Belegzellen", gespeist aus
                       `p.beleg_zellen.length`. Das war die dritte Zahl in einer
                       Zeile mit zwei Zahlen — und sie sah aus wie die
                       Wiederholung der ersten: an 42 von 42 Vorschlägen im
                       Bestand ist sie mit `n_reference_cells` identisch.

                       🔴 Sie ist es aber NICHT. Am Quelltext nachgelesen:
                       `n_reference_cells` zählt JEDE korrigierte Zelle
                       (reflection.py:778, vor dem `continue`), `beleg_zellen`
                       entsteht nur aus den Clustern — und in einen Cluster
                       kommt eine Zelle erst, wenn Modell- und Menschwert
                       AUSEINANDERGEHEN. Gemessen gilt an allen 42:
                       beleg_zellen = n_reference_cells − n_unchanged, und
                       n_unchanged ist im Bestand überall 0.

                       Die Gleichheit ist also ein Zufall des Bestands, keine
                       Eigenschaft. Zwei Namen für scheinbar dieselbe Zahl sind
                       genau die Vermischung, um die es hier geht — deshalb
                       steht jetzt der UNTERSCHIED da, und nur dann, wenn es
                       einen gibt. -->
                  <span v-if="bestaetigteZellen(p)">
                    · {{ bestaetigteZellen(p) }} davon bestätigten den Modellwert
                  </span>
                  <!-- Gegen WELCHEN Prompt die Regel formuliert wurde. Das Feld
                       wird seit R7.3 mitgeschrieben (diagnose.basis_prompt_version)
                       und vom Router ausgeliefert — die Seite hat es nur nie
                       referenziert. Ohne die Angabe ist eine ältere Regel nicht
                       einzuordnen: sie sagt "hänge Regel 12 an", und ob die
                       Basis dafür v3 oder v9 war, entscheidet, ob sie heute noch
                       trifft. -->
                  <span v-if="p.diagnose && p.diagnose.basis_prompt_version">
                    · formuliert gegen v{{ p.diagnose.basis_prompt_version }}
                  </span>
                </div>

                <!-- ACHTUNG: hier standen bis zum 13.08. (spaeter am Tag)
                     ZWEI Dinge, beide gestrichen (Entscheidung Simon):

                     1. Der Vorbehalt zur Datengrundlage als rote Zeile. Die
                        Pruefung dahinter ist am 14.08. ersatzlos entfernt
                        worden (Entscheidung Simon); die Seite spricht
                        seither keinen Vorbehalt zur Datengrundlage aus.

                     2. Die Clusterliste. Sie war die Wiederholung der
                        Tabelle im Diagnose-Abschnitt; auf drei Eintraege
                        gekuerzt war sie weder vollstaendig noch aussagekraeftig
                        -- gerade genug, um Platz zu kosten.

                     Was bleibt, ist genau das, was dieser Block EXKLUSIV
                     traegt und nirgendwo sonst steht: der eingefrorene Stand
                     (Zellen, Nachrichten, Belegzellen, Basisversion) und die
                     Belegtexte mit ihrem Gespraechsverlauf. -->

                <!-- Die Belege sind zurück, und der Grund für ihre Streichung
                     am 10.08. ist der Grund für ihre Rückkehr: damals waren es
                     drei Chat-Nachrichten OHNE ihren Gesprächsverlauf, also
                     genau der Ausschnitt, aus dem ein Mensch nichts schließen
                     soll. Seit Befund B2 (11.08.) führt jeder Beleg den
                     Verlauf mit, den auch der Bewerter gesehen hat — damit ist
                     es dieselbe Grundlage, auf der die Korrektur entstand.

                     ACHTUNG, warum ein Vorschlag OHNE Verlauf seine Texte
                     trotzdem nicht zeigt: für ihn gilt die Begründung von
                     damals unverändert. Er bekommt stattdessen den Vermerk —
                     sonst sähe ein alter Vorschlag aus wie ein neuer, und
                     genau der Unterschied ist das, was B2 belegen soll. -->
                <div v-if="belegBloecke(p).length" class="mt-3 pt-3 border-t border-gray-200">
                  <details class="group">
                    <summary class="flex items-start gap-1.5 cursor-pointer select-none text-xs text-gray-700 hover:text-gray-900 list-none [&::-webkit-details-marker]:hidden">
                      <ChevronRightIcon class="w-3 h-3 shrink-0 mt-0.5 transition-transform group-open:rotate-90" aria-hidden="true" />
                      <span>
                        <span class="font-semibold">Die Nachrichten, aus denen die Regel entstand</span>
                        <!-- Zwei Zahlen und nicht eine: ein Beleg KANN ohne
                             Verlauf sein (Nachricht allein im Kanal). "3 mit
                             Gesprächsverlauf" wäre dann für einen davon
                             falsch, und die Karte behauptete eine Grundlage,
                             die der Optimierer nicht hatte. -->
                        <span class="text-gray-500">
                          — {{ belegBloecke(p).length }} {{ belegHerkunft(p) }}<span
                            v-if="belegeMitVerlauf(p) !== null"
                        >, {{ belegeMitVerlauf(p) }} davon mit Gesprächsverlauf</span>
                        </span>
                      </span>
                    </summary>
                    <pre
                        v-for="(block, bi) in belegBloecke(p)"
                        :key="bi"
                        class="mt-2 p-2 bg-white border border-gray-200 rounded text-xs text-gray-800 whitespace-pre-wrap break-words font-sans leading-relaxed"
                    >{{ block }}</pre>
                  </details>
                </div>
                <!-- ⚠️ Hier stand bis zum 13.08. ein vierzeiliger Absatz, und
                     sein letzter Halbsatz war FALSCH: "einzeln nachlesbar sind
                     sie über die ids oben". Die ids stehen seit dem 10.08.
                     nicht mehr in der Fläche, sondern nur noch im Tooltip der
                     Spalte "Nachrichten" — und der gehört zur LIVE-Diagnose,
                     also zum heutigen Bestand und nicht zu diesem
                     eingefrorenen Vorschlag. Der Verweis schickte den Leser
                     an eine Stelle, an der er etwas anderes findet als
                     angekündigt.

                     Der Rest ist auf einen Halbsatz zusammengezogen. Was er
                     leisten muss, leistet er weiterhin: ein Vorschlag von VOR
                     B2 darf nicht aussehen wie einer danach — sonst ist genau
                     der Unterschied unsichtbar, den B2 belegen soll. -->
                <p v-else-if="p.diagnose && (p.diagnose.cluster || []).length" class="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                  <span class="font-semibold">Ohne Gesprächsverlauf</span> erzeugt — die
                  kompletten Nachrichten werden deshalb nicht gezeigt.
                </p>
              </div>
            </div>

            <!-- Aktionen -->
            <div class="shrink-0 md:w-64 md:text-right flex flex-col items-stretch md:items-end gap-2">
              <!-- ACHTUNG, der Grund für die sichtbaren Sätze unter BEIDEN
                   Knöpfen: ein deaktivierter Knopf verschluckt in Chrome und
                   Firefox jedes Mausereignis, und es blubbert auch nicht zur
                   Hülle hoch. Ein Tooltip erscheint dort also NIE — genau dann
                   nicht, wenn ihn jemand braucht: der gesperrte Knopf IST die
                   Frage "warum nicht?". Deshalb tragen die Tooltips nur den
                   Normalfall, und der Sperrgrund steht als Text da.
                   Bis zum 09.08. galt das nur für "Übernehmen"; "Verwerfen"
                   war unter status !== 'neu' grau und nannte keinen Grund --
                   und zwar direkt unter diesem Kommentar. -->
              <BaseTooltip
                  text="Nimmt den Vorschlag aus der Liste. Gelöscht wird nichts — was ein Mensch verworfen hat, ist selbst ein Befund."
                  wrapper-class="w-full md:w-auto"
              >
                <BaseButton
                    class="w-full"
                    :variant="discardBlockReason(p) ? 'disabled' : 'neutral'"
                    :disabled="!!discardBlockReason(p) || discardingId === p.id"
                    @click="askDiscard(p)"
                >
                  {{ discardingId === p.id ? "Verwerfe…" : "Verwerfen" }}
                </BaseButton>
              </BaseTooltip>

              <p v-if="discardBlockReason(p)" class="text-xs text-gray-500 md:text-right">
                {{ discardBlockReason(p) }}
              </p>

              <BaseTooltip
                  text="Macht aus dieser Regel einen Prompt-Entwurf. An der laufenden Bewertung ändert sich nichts."
                  wrapper-class="w-full md:w-auto"
              >
                <BaseButton
                    class="w-full"
                    :variant="p.adopt_block_reason ? 'disabled' : 'switch'"
                    :disabled="!!p.adopt_block_reason || adoptingId === p.id || pruefeId === p.id"
                    @click="askAdopt(p)"
                >
                  {{ pruefeId === p.id ? "Prüfe…" : adoptingId === p.id ? "Übernehme…" : "Als Entwurf übernehmen" }}
                </BaseButton>
              </BaseTooltip>

              <!-- Was der Frischecheck ergeben hat. Steht ÜBER dem
                   Sperrgrund: er erklärt, warum der Klick gerade nichts
                   getan hat, und das ist die jüngere und dringendere
                   Auskunft. -->
              <p v-if="frischHinweis[p.id]" class="text-xs text-amber-800 md:text-left">
                {{ frischHinweis[p.id] }}
              </p>

              <!-- ⚠️ LINKSBÜNDIG, anders als der Nachbar darüber. Die
                   Drift- und Formatgründe sind lange Sätze; 300 Zeichen
                   rechtsbündig in 16rem sind unlesbar, und gerade sie muss
                   jemand lesen können. -->
              <p v-if="p.adopt_block_reason" class="text-xs text-gray-500 md:text-left">
                {{ p.adopt_block_reason }}
              </p>
            </div>
          </div>
        </li>
      </ul>

      <!-- Der Ausklapper für die älteren Vorschläge. Er trägt die ZAHL, weil
           eine zugeklappte Liste sonst nichts über ihren Umfang sagt: „Ältere
           anzeigen" ohne Zahl ist ein Angebot ohne Gegenstand, und niemand
           kann daran sehen, ob dahinter zwei oder vierzig stehen.

           ⚠️ `v-if` an `aeltereProposals.length`: stehen ohnehin alle offenen
           Vorschläge da, wäre der Knopf keine Hilfe, sondern eine Zusage über
           etwas, das es nicht gibt — er klappte auf, was schon offen ist.

           ⚠️ Dieselbe Bauweise wie die beiden Aufklapper AN der Karte
           (`toggleProposal`, `toggleVorschau`): ein <button> mit
           `aria-expanded`, ausdrücklich KEIN <details>. Ein <details> müsste
           die älteren Karten umschließen, und die Karte steht als EIN v-for
           da — ein zweiter Block wäre eine zweite Fassung derselben Karte mit
           denselben Knöpfen. Zwei Bedienstellen für dieselbe Sache sind zwei
           Gelegenheiten, sie auseinanderlaufen zu lassen. -->
      <button
          v-if="aeltereProposals.length"
          type="button"
          class="mt-3 inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
          :aria-expanded="zeigeAeltereVorschlaege ? 'true' : 'false'"
          @click="toggleAeltereVorschlaege"
      >
        <ChevronRightIcon
            class="w-3.5 h-3.5 shrink-0 transition-transform"
            :class="zeigeAeltereVorschlaege ? 'rotate-90' : ''"
            aria-hidden="true"
        />
        <span class="underline">{{ aeltereLabel }}</span>
      </button>

      <!-- Der Leerzustand muss den Fall abdecken, den die Filterung erst
           erzeugt hat: alle Vorschläge sind übernommen, und dieser Block ist
           deshalb leer, obwohl es Vorschläge gibt. Ohne den Verweis nach
           unten liest sich das als "es ist nichts passiert". -->
      <p v-if="!loading && !error && offeneProposals.length === 0" class="text-gray-500 text-sm mt-4">
        <!-- Zwei ganze Sätze, kein Einschub: Vue trennt den Textknoten vom
             <template> mit einem Leerzeichen, und ein angehängtes „; …“ stünde
             dann als „darüber ; die“ da. -->
        Kein Vorschlag offen. Neue entstehen über „Vorschlag erzeugen“ im Block darüber.
        <template v-if="adoptedProposals.length">Die bereits übernommenen stehen unter „Entwürfe“.</template>
      </p>
    </section>

    <!-- ======================= 4. Was daraus wurde ======================= -->
    <section>
      <h3 class="flex items-center gap-1.5 text-lg font-semibold text-gray-800 mb-3">
        <span>Entwürfe <span class="font-normal text-gray-500">— und ihr Messergebnis</span></span>
        <BaseInfoIcon :text="HILFE_ERGEBNIS" label="Entwürfe und Messergebnis" :oben="false" />
      </h3>

      <div v-for="p in adoptedProposals" :key="'ergebnis-' + p.id" class="mb-4 border border-gray-200 rounded-lg p-3">
        <!-- Schriftgrößen wie auf der Versionsseite: dort ist die Version die
             Hauptsache der Zeile (text-lg), der Schlüssel die Nebensache. Der
             Übernehmen-Pfad leitet genau dorthin um — wer zwischen beiden
             Seiten wechselt, soll dieselbe Sache gleich groß wiederfinden. -->
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-lg font-medium text-gray-900">
            v{{ p.prompt_version.version }}
          </span>
          <span class="text-sm text-gray-500">{{ p.prompt_version.prompt_key }}</span>
          <BaseTooltip :text="gateStatusHilfe(p.prompt_version.gate_status)">
            <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold cursor-help"
                :class="statusClass(p.prompt_version.gate_status)"
            >
              {{ statusText(p.prompt_version.gate_status) }}
            </span>
          </BaseTooltip>
          <span
              v-if="p.prompt_version.is_active"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700"
          >
            aktiv
          </span>
          <router-link
              to="/async/prompt-versions"
              class="text-xs underline text-indigo-600 hover:text-indigo-800"
          >
            in Prompt versions ansehen
          </router-link>
        </div>

        <p class="mt-1 text-xs text-gray-600 whitespace-pre-line break-words">{{ p.suggestion }}</p>

        <!-- ACHTUNG, die Klassen bleiben auf EINER Zeile mit dem Element: der
             Formtest bestimmt die Reichweite eines Scroll-Containers über die
             Einrückung, und ein umgebrochenes class-Attribut läge tiefer als
             seine eigenen Kinder. Der Container wäre dann für die Prüfung
             leer, und ein fehlendes 'fixiert' fiele niemandem auf. -->
        <div v-if="metricRows(p.prompt_version.gate_metrics).length" class="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto">
          <table class="w-full text-xs font-mono">
            <thead>
              <tr class="border-b border-gray-300">
                <th class="text-left py-1 pr-4">Kennzahl</th>
                <th class="text-right py-1 pl-2">Wert</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in metricRows(p.prompt_version.gate_metrics)" :key="row.name" class="border-t border-gray-200">
                <td class="py-1 pr-4">
                  <!-- fixiert, aus demselben Grund wie oben: der Kasten ist ein
                       overflow-x-auto und beschneidet auch nach oben. -->
                  <BaseTooltip :text="row.hilfe" fixiert>
                    <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                      {{ row.name }}
                    </span>
                  </BaseTooltip>
                </td>
                <td class="text-right py-1 pl-2 text-gray-800">{{ row.value }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Der Satzname bleibt SICHTBAR und wandert nicht mit ins Tooltip: er
             ist die Bedingung dafür, dass die Richtungskennzahlen überhaupt
             etwas zu rechnen haben. Gegen einen Satz ohne Korrekturen kommt
             der Vergleich mit n_human_cells = 0 zurück, und das sieht aus wie
             ein Ergebnis, obwohl es die falsche Frage war.
             ACHTUNG, der zweite Zweig: hier stand "gegen den
             Standard-Vergleichssatz", als wäre das der harmlose Normalfall.
             Das Backend sagt über genau diesen Satz das Gegenteil -- 'default'
             traegt keine Korrekturen (reflection.py:68-71). Der Zweig darf
             also nicht beruhigen, sondern muss warnen. -->
        <p v-else class="mt-2 text-xs text-gray-500">
          <template v-if="p.set_name">
            Noch nicht gemessen. Vorgesehener Vergleichssatz:
            <span class="font-mono">{{ p.set_name }}</span>.
          </template>
          <template v-else>
            Noch nicht gemessen. Ohne Herkunftssatz misst der Vergleich gegen
            <span class="font-mono">default</span> — dort gibt es keine Korrekturen, und die
            Richtungskennzahlen bleiben leer.
          </template>
          <BaseInfoIcon
              :text="HILFE_NICHT_GEMESSEN"
              label="noch nicht gemessen"
              wrapper-class="align-text-bottom"
          />
        </p>
      </div>

      <!-- !error: sonst steht unter dem roten Fehlerkasten eine Behauptung
           über Daten, die nie angekommen sind. Der Abschnitt darüber macht es
           schon richtig. -->
      <p v-if="!loading && !error && adoptedProposals.length === 0" class="text-gray-500 text-sm mt-4">
        Noch kein Vorschlag übernommen. Gemessen wird in
        <router-link to="/async/prompt-versions" class="underline text-indigo-600 hover:text-indigo-800">
          Prompt versions
        </router-link>.
      </p>
    </section>

    <!-- Ein Modal für zwei Handlungen. Titel, Text und Knopfbeschriftung
         hängen an pendingAction — zwei getrennte Modals wären zweimal
         dieselbe Mechanik mit zwei Gelegenheiten, sie unterschiedlich falsch
         zu machen. -->
    <!-- cancelLabel ausdrücklich: die Vorgabe von ConfirmModal ist "Cancel",
         und der stünde als einziges englisches Wort in einem sonst deutschen
         Dialog — direkt neben „Übernehmen“. -->
    <ConfirmModal
        :isOpen="showConfirm"
        :title="confirmTitle"
        :message="confirmMessage"
        :confirmLabel="confirmLabel"
        :confirmVariant="confirmVariant"
        cancelLabel="Abbrechen"
        @confirm="confirmed"
        @cancel="cancelConfirm"
    />
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import BaseTooltip from "@/BaseComponents/BaseTooltip.vue";
import ConfirmModal from "@/BaseComponents/ConfirmModal.vue";
import {
  ArrowPathIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from "@heroicons/vue/24/outline";
import {
  getDiagnose,
  getOptimizerPrompt,
  saveOptimizerPrompt,
  createEvalSet,
  listEvalSets,
  listProposals,
  getOptimizerModels,
  messeMetaPrompt,
  proposeRule,
  discardProposal,
  adoptProposal,
} from "../api/reflection";
// Aus der Nachbar-API und nicht aus einer zweiten eigenen: welcher Prompt
// wirkt, ist EINE Frage, und zwei Endpunkte darauf liefen auseinander.
import { getEffectivePromptKey } from "../api/promptVersions";
// Die Maße des Prompts kommen aus EINEM Modul, das sich die Versionsseite mit
// dieser hier teilt. Zwei Zählungen für dieselbe Sache wären zwei Wahrheiten —
// derselbe Fehler wie mit MIN_REFERENZ_ZELLEN weiter unten, nur früher bemerkt.
import {
  HILFE_REGELN,
  HILFE_ZEICHEN,
  REGELN_WARN,
  PROMPT_WARN_ZEICHEN,
  regelAnzahl,
  promptZeichen,
  promptWarnung,
} from "../promptMass";
// belegMasse.js trägt seit dem 19.08. nur noch den Tooltip am Feld. Die
// Schaetzung der Belegmasse ist mit ihrer Warnung gestrichen: sie zaehlte
// Zeichen und behauptete damit etwas ueber Token. Was hier wirklich misst,
// ist tokenMessung -- gegen das Modell, in Token.
import { hilfeBelegbudget } from "../belegMasse";

// Das dritte Massmodul, und es rechnet ausdruecklich nichts: promptMass
// ZAEHLT Zeichen, tokenMessung ZEIGT gemessene Token. Zwei Fragen, zwei
// Namen -- ein gemeinsames Modul lüde dazu ein, die eine Zahl für die
// andere zu halten.
//
// ACHTUNG, am 20.08. korrigiert: hier stand "belegMasse SCHAETZT Zeichen"
// -- sechs Zeilen unter dem Kommentar darüber, der das Gegenteil sagt.
// Die Schaetzung ist seit dem 19.08. gestrichen.
import { messsatz, messvorbehalt, messwarnung, HILFE_MESSEN } from "../tokenMessung";
// Die Merkliste — Schlüssel und Format stehen nur dort, nicht hier.
import {
  lesen as merklisteLesen,
  abonnieren as merklisteAbonnieren,
  MERK_MAX,
} from "../merkliste";

// Unter so vielen korrigierten Zellen wird keine Kennzahl mehr gezeigt,
// sondern der Satz "zu wenig Material". Dieselbe Regel wie die
// Unterpower-Sperre aus R4: eine mittlere Annaeherung ueber eine Handvoll
// Zellen ist keine Kennzahl, und wer die Zahl nicht danebenstehen hat, kann
// das nicht sehen. Der Wert ist bewusst klein -- er markiert nicht "genug",
// sondern "offensichtlich zu wenig".
//
// ACHTUNG: NICHT mehr massgeblich. Seit dem 10.08. liefert die Diagnose die
// wirksame Schwelle mit (min_reference_cells, aus OPTIMIZER_MIN_REFERENZZELLEN);
// diese Konstante ist nur noch der Rueckfall fuer eingefrorene Diagnosen ohne
// das Feld -- und der Vorgabewert in den Hilfetexten unten, die beim Laden des
// Moduls entstehen und deshalb keine Antwort abwarten koennen.
const MIN_REFERENZ_ZELLEN = 10;

// Ab wie vielen Zeichen die Notiz der aktiven Optimierer-Fassung in der Zeile
// unter dem Textfeld gekuerzt wird.
//
// ACHTUNG, das ist eine Konvention und ausdruecklich KEINE Messung -- wie
// REGELN_WARN drueben in promptMass.js. Der Wert ist an dem Bestand gewaehlt,
// der da ist: die aktive Fassung fuehrt eine Notiz von 190 Zeichen und lief in
// der schmalen Spalte ueber drei Zeilen; die kurzen Notizen der uebrigen
// Fassungen bleiben darunter und werden gar nicht erst angefasst.
//
// Gekuerzt wird nur die ANZEIGE. In der Datenbank steht die Notiz vollstaendig,
// und der Tooltip zeigt sie auch vollstaendig -- eine Notiz ist die Begruendung
// einer Fassung, nicht Zierat.
const NOTIZ_MAX_ZEICHEN = 60;

// Wie viele Regelvorschlaege ohne einen Klick dastehen. Alles aeltere steht
// hinter dem Ausklapper unter der Liste -- nicht weg, nur nicht im Weg.
//
// ACHTUNG, das ist eine Konvention und ausdruecklich KEINE Messung -- wie
// NOTIZ_MAX_ZEICHEN darueber. Anlass ist der Bestand: 39 offene Vorschlaege
// schoben den Abschnitt "Entwuerfe" um mehrere Bildschirme nach unten, und
// jeder Optimierer-Lauf legt einen weiteren dazu.
//
// ACHTUNG, was die Zahl voraussetzt: list_proposals liefert NEUESTE ZUERST
// (order_by(OptimizerProposal.id.desc())). Die ersten fuenf sind deshalb die
// juengsten -- sortierte der Endpunkt anders, schnitte der Ausschnitt hier die
// falschen weg.
//
// EINE Zahl, EINE Quelle: sichtbareProposals und aeltereProposals lesen beide
// diese Konstante. Zwei getrennt geschriebene Grenzen liefen auseinander, und
// dann zeigte die Liste sechs und der Ausklapper zaehlte ab fuenf -- genau der
// Fehler, den promptMass.js fuer die Prompt-Masse abgestellt hat.
const VORSCHLAEGE_SICHTBAR = 5;

// ACHTUNG: hier standen bis zum 13.08. WARNUNG_KURZ_MAX = 110 und
// CLUSTER_SICHTBAR = 3. Beide sind mit ihren Verbrauchern gefallen, als die
// Vorschlagskarte den Vorbehalt und die Clusterliste abgab. Der Vorbehalt
// zur Datengrundlage ist am 14.08. ganz entfallen (Entscheidung Simon).
//
// Der Kreislauf, als Kette statt als Textblock.
//
// ACHTUNG, was diese Tooltips NICHT mehr tun: die Schritte zweitklaerten.
// Bis zum 09.08. sagte die Pille "Diagnose" fast wortgleich dasselbe wie der
// Erklaertext am Abschnitt darunter -- zwei Icons, ein Inhalt, einen halben
// Bildschirm auseinander. Die Kette ist jetzt ein INHALTSVERZEICHNIS: sie
// sagt, wo ein Schritt stattfindet, und ueberlaesst das Was dem Abschnitt.
const KREISLAUF = [
  {
    titel: "Korrektur",
    ziel: "/async/messages",
    hilfe:
      "Jemand korrigiert eine Modellbewertung in der Nachrichtenliste — der " +
      "einzige Schritt, den ein Mensch inhaltlich macht. Klick öffnet Messages.",
  },
  {
    titel: "Diagnose",
    hilfe: "Erster Abschnitt: die Korrekturen, zu Clustern zusammengefasst.",
  },
  {
    titel: "Regelvorschlag",
    hilfe:
      "Zweiter und dritter Abschnitt: ein Modell formuliert aus den Clustern " +
      "eine Regel, angeleitet vom Optimierer-Prompt.",
  },
  {
    titel: "Entwurf",
    hilfe:
      "Vierter Abschnitt: aus dem übernommenen Vorschlag ist eine Prompt-Version " +
      "geworden. Wirksam ist sie damit noch nicht.",
  },
  {
    titel: "Vergleich",
    ziel: "/async/prompt-versions",
    hilfe:
      "Der Regressionsvergleich misst den Entwurf gegen die aktive Version — auch, " +
      "ob er näher an die menschliche Korrektur rückt. Klick öffnet Prompt versions.",
  },
];

// Die Erklaerungen zu den vier Abschnitten. Sie standen bis zum 09.08. als
// Absaetze unter den Ueberschriften und schoben zusammen mit dem Kopf rund
// eine halbe Bildschirmhoehe vor die eigentliche Arbeit.
//
// ACHTUNG, zwei Regeln fuer alles, was hier steht:
//  (1) NICHT hierher darf, was einen Vorbehalt ueber die Zahlen daneben
//      ausspricht -- "zu wenig Material", die
//      Sperrgruende an den Knoepfen, der Vergleichssatz, der Leerzustand der
//      Clustertabelle. Ein Tooltip erscheint erst beim Ueberfahren, und wer
//      ihn nicht sucht, liest die Zahl ohne ihn.
//  (2) UNTER 200 Zeichen. Der Kasten ist 16rem breit bei 11px Schrift, also
//      rund 44 Zeichen je Zeile; 400 Zeichen ergeben elf Zeilen, und ein
//      fixierter Tooltip schliesst bei der kleinsten Scrollbewegung -- man
//      kann ihn also nicht einmal zu Ende lesen.
const HILFE_DIAGNOSE =
  "Jede Zeile ist ein wiederkehrender Unterschied zwischen Modellwert und " +
  "Korrektur: gleiches Kriterium, gleiche Richtung. Eine Häufung zeigt ein " +
  "Muster, keine Ursache.";

const HILFE_ZU_WENIG =
  "Aus so wenigen Zellen entsteht kein Muster, sondern Zufall. Die Cluster " +
  "darunter sind Anschauung, keine Aussage — deshalb steht hier kein Mittelwert.";

const HILFE_OPTIMIERER =
  "Die Anweisung, mit der ein Modell aus den Clustern darüber eine Regel " +
  "formuliert. Speichern legt eine neue Version an und aktiviert sie; die " +
  "vorherige bleibt stehen.";

const HILFE_VORSCHLAEGE =
  "Ein Vorschlag ist eine Regel, kein Beschluss. Übernehmen macht daraus einen " +
  "Prompt-Entwurf (Zustand „draft“); wirksam wird er erst nach einem bestandenen " +
  "Vergleich.";

const HILFE_ERGEBNIS =
  "Die Kette in einer Zeile: diese Korrekturen → diese Regel → dieses " +
  "Messergebnis. Gestartet und aktiviert wird in Prompt versions; hier steht " +
  "nur, was dort herauskam.";

const HILFE_NICHT_GEMESSEN =
  "Der Entwurf steht, gemessen wurde noch nichts. Den Vergleich startet man in " +
  "Prompt versions; welchen Vergleichssatz er nimmt, löst das Backend aus der " +
  "Herkunft dieser Version auf.";

// Ausnahme von Regel (1) oben: dieser Vorbehalt hängt an einem Icon. Er darf
// es, weil der Satz DANEBEN die Auskunft schon trägt ("Diesen Vergleichssatz
// gibt es nicht") -- im Tooltip steht nur, woher ein Satz sonst kommt.
// Welcher Prompt hier fortgeschrieben wird -- und warum die Seite das sagen
// muss. Wortgleich zur Versionsseite: es ist dieselbe Auskunft, und zwei
// Formulierungen dafuer laesen sich wie zwei Sachverhalte.
const HILFE_WIRKSAM =
  "Welcher der beiden Bewertungsprompts gilt, entscheidet der Modus " +
  "(async_rating_mode): LLM, RAG — oder ML, dann keiner.\n\n" +
  "Angehängt wird an den, der gerade bewertet. Sonst entstünde aus Korrekturen des " +
  "einen eine Regel am anderen: der Vergleich liefe durch, die Bewertung bliebe gleich.";

const HILFE_MODUS_ML =
  "Im Modus ML bewertet ein trainiertes Modell ohne Prompt. Es gibt nichts, was eine " +
  "Regel fortschreiben könnte — der Lauf ist gesperrt, nicht kaputt.\n\n" +
  "Abgelegte Vorschläge bleiben und lassen sich übernehmen, sobald der Modus wieder " +
  "auf LLM oder RAG steht.";

// Ein Vorschlag, der an einem Prompt haengt, der gerade nicht bewertet.
const HILFE_ANDERER_SCHLUESSEL =
  "Dieser Vorschlag ist gegen einen anderen Prompt formuliert worden — der Modus ist " +
  "seither umgestellt.\n\n" +
  "Übernehmen hängt die Regel an den Prompt, aus dem sie entstand. Wirksam wird sie " +
  "erst, wenn der Modus dorthin zurückkehrt.";

// Die beiden Modi. Sie stehen an EINER Stelle, weil sie zusammen gelesen
// werden müssen: der zweite ergibt nur Sinn, wenn man weiß, was der erste tut.
const HILFE_MODUS_APPEND =
  "Eine zusätzliche Regel, hinter die letzte Regel des Blocks gesetzt. Sonst " +
  "bleibt der Prompt Zeichen für Zeichen stehen — nur so misst der Vergleich " +
  "danach eine einzelne Ursache.\n\n" +
  "Der Preis: der Prompt wird länger, und nichts prüft, ob die neue Regel " +
  "einer bestehenden widerspricht.";

const HILFE_MODUS_REVISE =
  "Der Optimierer nennt eine bestehende Regelnummer und formuliert sie neu — " +
  "oder streicht sie. Genau eine Regel, damit der Vergleich weiterhin eine " +
  "einzelne Ursache misst.\n\n" +
  "Verfehlt das Modell das Antwortformat, steht der Vorschlag als „Format " +
  "verfehlt“ da und ist gesperrt. Er fällt nicht still aufs Anhängen zurück.";

// Woher ein Vorschlag stammt, der keine Optimierer-Version trägt.
const HILFE_REGELPROMPT =
  "Der Meta-Prompt für „Überarbeiten“ steht als Konstante im Code und ist hier " +
  "nicht editierbar: optimizer_prompt_versions hat kein Feld für die Art des " +
  "Prompts und lässt nur eine aktive Zeile zu.\n\n" +
  "Statt einer Versionsnummer trägt der Vorschlag den Hash seines " +
  "Meta-Prompts. Wer ihn ändert, ändert Code und baut ein Image.";

// Der Vorschlag ist gegen eine andere Fassung formuliert worden.
const HILFE_ANDERE_BASIS =
  "Seit der Erzeugung ist eine andere Version aktiv geworden. Das sperrt den " +
  "Vorschlag nicht: gesperrt wird nur, wenn die Handlung selbst nicht mehr " +
  "passt — Zielregel weg oder umformuliert, oder die Nummer vergeben.\n\n" +
  "Strenger gefasst entwertete jede Aktivierung sämtliche offenen Vorschläge.";

const HILFE_UNBEKANNTER_SATZ =
  "Dieser Name hat keine Zeile in prompt_eval_messages. Die Auswahl daneben " +
  "führt alle vorhandenen Sätze; einen neuen legt das Pluszeichen an.";

const HILFE_NEUER_SATZ =
  "Er nimmt zuerst jede Nachricht mit Korrekturen auf und füllt dann mit " +
  "bewerteten ohne Korrektur auf die Zielgröße auf. Danach ist er eingefroren.";

// ACHTUNG: der Satz nennt ausdruecklich die stehen bleibenden Regeln. Genau
// daran hing der Anzeigefehler, den dieser Block behebt -- die Karte zeigte
// Regel 5 alt und neu, und der Widerspruch entstand zur unveraenderten
// Regel 6, die nirgends stand.
const HILFE_VORSCHAU =
  "Der ganze Bewertungsprompt, wie er nach dem Übernehmen aussähe. Nur so ist " +
  "zu sehen, ob die neue Regel den stehen bleibenden widerspricht. Gerechnet " +
  "gegen die heute aktive Version.";

// ACHTUNG: die beiden Sätze stehen an der Karte, nicht im Banner oben — bei
// einer langen Liste ist das Banner beliebig weit weg von dem Knopf, der
// gerade nicht getan hat, was er sollte. Und nicht im Tooltip: der Knopf kann
// gesperrt sein, und dann erscheint keiner.
const HINWEIS_VORSCHAU_GEWECHSELT =
  "Übernommen wurde nichts: seit dem Laden ist eine andere Version aktiv " +
  "geworden, und aus dem Vorschlag entstünde jetzt ein anderer Text. Die " +
  "Vorschau darunter ist neu gerechnet — ansehen und dann erneut übernehmen.";

const HINWEIS_ZEILE_WEG =
  "Übernommen wurde nichts: diesen Vorschlag führt die Liste nicht mehr. " +
  "Vermutlich hat ihn jemand anders inzwischen entschieden.";

// ACHTUNG: der erste Absatz hiess bis zum 09.08. "bis R7 war die menschliche
// Korrektur ein Endpunkt ...". Das ist ein Eintrag fuer den Thesis-Leser, kein
// Bedienwissen -- er beantwortet die Frage "warum gibt es diese Seite", die
// niemand stellt, der sie taeglich oeffnet.
const HILFE_KREISLAUF =
  "Diese Seite entscheidet nichts. Jeder Schritt endet an einem Knopf, den ein " +
  "Mensch drückt — und der letzte, das Aktivieren, liegt in Prompt versions.";

// Die Kennzahlen, die im Ergebnisblock gezeigt werden. Bewusst eine kleine
// Auswahl: die vollstaendige Tabelle steht auf der Versionsseite, und eine
// zweite vollstaendige Anzeige waere eine zweite Wahrheit ueber dieselbe
// Messung. Hier stehen die vier R7-Kennzahlen (die Richtung) plus
// changed_share (die Streuung), weil genau das die Frage dieser Seite ist:
// hat die Regel getan, was sie sollte, und nur das?
// ACHTUNG, die Versalien: bis zum 09.08. schrien dreizehn Woerter ueber zehn
// Tooltips ("ACHTUNG" 4x, BERICHTET, NETTO, AUSSERHALB, ERSETZT ...). Wenn
// jeder Tooltip schreit, hebt keiner mehr etwas hervor. Uebrig bleibt eine
// einzige Stelle: NEGATIV IST GUT -- eine echte Vorzeichenfalle, die jede
// andere Zahl der Tabelle invertiert.
const ZIEL_KENNZAHLEN = [
  {
    name: "n_human_cells",
    hilfe:
      "Zellen, an denen ein Mensch korrigiert hat und beide Läufe eine Zahl geliefert " +
      "haben. Steht zuerst: unter " + MIN_REFERENZ_ZELLEN + " Zellen sagt sie mehr als " +
      "jede Zahl darunter.",
  },
  {
    name: "human_gap_delta",
    hilfe:
      "Wie sich der mittlere Abstand zur menschlichen Korrektur ändert (Kandidat minus " +
      "aktive Version).\n\n" +
      "NEGATIV IST GUT — als einzige Zahl hier. Ohne Schwelle: wird berichtet, nicht " +
      "geurteilt.",
  },
  {
    name: "approach_share",
    hilfe:
      "(Zellen näher am Menschen minus Zellen weiter weg) / n_human_cells. Positiv ist " +
      "gut. Netto gerechnet: zwei näher und eine weiter ergeben 0,25, nicht 0,67.",
  },
  {
    name: "collateral_share",
    hilfe:
      "Anteil der geänderten Zellen außerhalb der Referenzmenge. Trennt „hat gezielt " +
      "gewirkt“ von „hat breit gestreut“ — ohne sie sagte eine Verbesserung nichts.",
  },
  {
    name: "changed_share",
    hilfe:
      "Anteil der Zellen, deren Wert sich zwischen den beiden Läufen unterscheidet — " +
      "die Hauptkennzahl des Vergleichs und die einzige hier mit einer Schwelle. Die " +
      "geltende Grenze steht in Prompt versions.",
  },
];

// Die Zustände, die als Pille erscheinen. Ohne Uebersetzung stuende auf einer
// deutschen Seite sichtbar "uebernommen" -- die ASCII-Schreibweise aus der
// Datenbank, direkt neben dem sauber gesetzten "Übernehmen" am Knopf.
const STATUS_TEXT = {
  neu: "neu",
  uebernommen: "übernommen",
  verworfen: "verworfen",
  draft: "Entwurf",
  passed: "bestanden",
  failed: "durchgefallen",
  running: "läuft",
  baseline: "Baseline",
};

// Kurzauskunft zum Gate-Zustand. Die Versionsseite hat dafuer eine eigene,
// ausfuehrlichere Tabelle; hier genuegt der Satz, der die Frage beantwortet,
// die ein rotes "durchgefallen" ausloest.
const GATE_STATUS_HILFE = {
  draft: "Entwurf — noch kein Vergleich gefahren.",
  running: "Der Vergleich läuft gerade.",
  passed: "Der Vergleich ist bestanden. Aktiviert wird trotzdem von Hand.",
  failed: "Der Vergleich ist nicht bestanden. Die Gründe stehen in Prompt versions.",
  baseline: "Die Ausgangsversion, gegen die verglichen wird.",
};

export default {
  components: {
    BaseButton,
    BaseInfoIcon,
    BaseTooltip,
    ConfirmModal,
    ArrowPathIcon,
    ChevronRightIcon,
    ExclamationTriangleIcon,
    PlusIcon,
  },

  data() {
    return {
      // Konstanten, die das Template braucht. Sie stehen als Modulkonstanten
      // oben und werden hier nur durchgereicht — Fließtext gehört nicht
      // zwischen die Zustandsfelder.
      KREISLAUF,
      HILFE_KREISLAUF,
      HILFE_DIAGNOSE,
      HILFE_ZU_WENIG,
      HILFE_OPTIMIERER,
      HILFE_VORSCHLAEGE,
      HILFE_ERGEBNIS,
      HILFE_NICHT_GEMESSEN,
      HILFE_UNBEKANNTER_SATZ,
      HILFE_NEUER_SATZ,
      HILFE_VORSCHAU,
      HILFE_WIRKSAM,
      HILFE_MODUS_ML,
      HILFE_ANDERER_SCHLUESSEL,
      HILFE_MODUS_APPEND,
      HILFE_MODUS_REVISE,
      HILFE_REGELPROMPT,
      HILFE_ANDERE_BASIS,
      HILFE_REGELN,
      HILFE_ZEICHEN,
      HILFE_MESSEN,
      MERK_MAX,
      MIN_REFERENZ_ZELLEN,

      setName: "korrektur-01",

      // ⚠️ LEER, und das ist der Punkt. Bis zum 18.08. stand hier
      // "gemma3:4b" — eine zweite Wahrheit neben OPTIMIZER_MODEL im
      // async-Backend (app/services/reflection.py). Die beiden konnten
      // auseinanderlaufen, ohne dass es jemandem auffiel: die Oberfläche
      // zeigte den einen Namen, gefahren wurde der andere.
      // Gesetzt wird der Wert jetzt aus der Antwort des Dienstes, der ihn
      // anwendet — und nur, solange niemand von Hand etwas eingetragen hat.
      model: "",

      // Die Modelle des Ollama, das den Lauf tatsächlich fährt.
      // Leer heißt "noch nicht geladen ODER nicht erreichbar" — die beiden
      // trennt modelleGeladen, denn ein leeres Dropdown wäre eine Aussage.
      modelle: [],
      modelleGeladen: false,
      modelleFehler: "",

      // Die letzte Tokenmessung, oder null.
      //
      // ⚠️ NICHT persistiert und bewusst flüchtig: sie gilt für genau die
      // Parameter, mit denen sie entstanden ist. Ändert sich einer davon,
      // wird sie verworfen — eine stehengebliebene Zahl zu einem geänderten
      // Budget wäre eine Falschauskunft, und zwar eine, die niemand
      // nachrechnet.
      messung: null,
      messungLaeuft: false,
      messungFehler: "",
      // OPTIMIZER_THINK des async-Backends. Nur zum Warnen: ein global
      // gesetztes true legt jeden Lauf eines Modells OHNE Denkmodus lahm
      // (HTTP 400), und das ist von hier aus nicht abstellbar.
      thinkGlobal: null,

      // Das Belegbudget. String, weil es aus einem <input type="number"> kommt
      // und "" von "0" unterscheidbar bleiben muss — genau wie die beiden
      // Kontextfelder in der Nachrichtenliste. "" heißt "nicht gesetzt" und
      // wird gar nicht erst mitgeschickt; "0" heißt "keine Belege".
      //
      // ACHTUNG, NICHT persistiert. Der Wert gilt für diesen Lauf; womit ein
      // Vorschlag entstanden ist, steht an ihm selbst (diagnose.beleg_budget)
      // und nirgends sonst.
      //
      // Startwert "" nur, solange nichts geladen ist: reload() belegt das Feld
      // danach mit belegmasse.max_belege vor (siehe dort). Eine Zahl HIER wäre
      // die zweite Wahrheit neben OPTIMIZER_MAX_BELEGE.
      belegbudget: "",

      // Die gemerkten Nachrichten, für den Vergleichssatz.
      merkliste: [],
      merklisteNutzen: true,
      merkAbbestellen: null,

      // Die Ausgabe des letzten Einfrierens, Zeile für Zeile. Sie führt die
      // Filterzähler und — im Merklistenweg — jede ID, die nicht mitgekommen
      // ist. Ein Satz ist danach nicht mehr zu ändern; wer das erst beim
      // Messen bemerkt, hat den Satz umsonst.
      letztesProtokoll: [],

      // "append" | "revise". Die Vorgabe ist der Weg von vor dem 11.08. — so
      // verhält sich die Seite unverändert, bis jemand ausdrücklich umschaltet,
      // und der Vorher/Nachher-Vergleich bleibt fahrbar.
      modus: "append",
      // Die beiden Anweisungen, die der Rahmen je Modus bekommt, samt Hash und
      // Platzhalter — vom Backend geliefert.
      //
      // ACHTUNG: NICHT vorbelegt, und keine Kopie der Texte hier. Sie sind
      // drüben Konstanten und werden dort eingesetzt; eine zweite Fassung an
      // dieser Stelle liefe beim nächsten Deploy auseinander, ohne dass es
      // jemandem auffiele. null heißt "nicht geladen" — dann zeigt die Seite
      // sie nicht und behauptet nichts über sie.
      instruktionen: null,
      // Ob das Backend den Modus "revise" überhaupt anbietet (OPTIMIZER_REVISE).
      // NICHT vorbelegt: solange nichts geladen ist, wird nichts behauptet.
      reviseEnabled: null,

      // Die vorhandenen Vergleichssätze [{set_name, n_messages}].
      evalSets: [],

      // Welcher Prompt gerade bewertet: {rating_mode, prompt_key, ...} oder
      // null, solange nichts geladen ist. NICHT vorbelegen — ein geratener
      // Schlüssel stünde als Tatsachenbehauptung über der Vorschlagsliste,
      // und genau diese Behauptung war der Fehler, den diese Anzeige behebt.
      wirksam: null,

      // Das Anlege-Formular. Die beiden Zahlen sind Vorgaben aus dem Backend
      // (freeze_referenz_set) und stehen hier nur zum Ändern — laufen sie
      // auseinander, entsteht der Satz mit anderen Zahlen, als die Seite
      // daneben behauptet.
      showNewSet: false,
      newSetName: "",
      newSetSize: 24,
      newSetMinRef: 8,
      creatingSet: false,

      diagnose: null,
      optimizerActive: null,
      optimizerVersions: [],
      optimizerContent: "",
      optimizerNote: "",
      // Der zuletzt vom Backend gelieferte Text. Gegen ihn wird verglichen, ob
      // im Feld etwas geändert wurde — ohne das wäre der Speichern-Knopf immer
      // frei und erzeugte 409 statt einer Rückmeldung.
      optimizerGeladen: "",

      proposals: [],

      loading: false,
      savingOptimizer: false,
      proposing: false,
      discardingId: null,
      adoptingId: null,
      // Läuft der Frischecheck vor dem Übernehmen-Dialog? Eigener Zustand
      // neben adoptingId: das eine ist „ich sehe nach, ob die Vorschau noch
      // stimmt", das andere „ich schreibe". Ein gemeinsamer Zustand nennte
      // beides „Übernehme…" und behauptete damit einen Schreibvorgang, der
      // noch gar nicht begonnen hat.
      pruefeId: null,

      error: "",
      savedMessage: "",
      // Der dritte Ton neben grün und rot: ein Ergebnis, das weder Erfolg noch
      // Panne ist. Ohne ihn landete "der Lauf lieferte keine Regel" im grünen
      // Kasten und läse sich als "hat geklappt".
      hinweis: "",

      // expandedCluster ist mit dem Beispiel-Aufklapper weggefallen (10.08.).
      expandedProposal: {},
      // Der zweite Aufklapper der Karte, mit EIGENEM Zustand: „Worauf das
      // beruht" zeigt die Herkunft, „Der Prompt danach" die Wirkung. Ein
      // gemeinsamer Zustand risse beim Aufklappen des einen den anderen mit
      // auf, und die Karte wäre auf einen Klick doppelt so lang.
      expandedVorschau: {},
      // Was der Frischecheck je Karte ergeben hat. Nach `id` geschlüsselt wie
      // expandedVorschau — die Liste selbst wird beim Nachladen komplett
      // ausgetauscht, ein Vermerk am Objekt ginge dabei verloren.
      frischHinweis: {},

      // Steht der Ausklapper der älteren Vorschläge offen? Zugeklappt als
      // Vorgabe — das ist der ganze Zweck der Begrenzung.
      //
      // ⚠️ NICHT nach `id` geschlüsselt wie expandedProposal und
      // expandedVorschau: dort gehört der Zustand zu einer Karte, hier zum
      // Abschnitt. Und ⚠️ reload() fasst ihn ausdrücklich NICHT an — wer
      // aufgeklappt hat und dann verwirft oder übernimmt, stünde sonst nach
      // jeder Handlung wieder vor der zugeklappten Liste und suchte die Karte
      // von neuem.
      zeigeAeltereVorschlaege: false,

      showConfirm: false,
      pendingProposal: null,
      // "discard" | "adopt" -- steuert Titel, Text und Knopfbeschriftung des
      // einen Modals.
      pendingAction: null,
    };
  },

  computed: {
    cluster() {
      return (this.diagnose && this.diagnose.cluster) || [];
    },

    // Die Maße, mit denen der Lauf die Belege kürzt — vom Backend geliefert,
    // nicht hier gesetzt. Fehlt die Angabe, bleibt es ein leeres Objekt, und
    // die Abschätzungen darunter liefern null statt einer geratenen Zahl.
    belegmasse() {
      return (this.instruktionen && this.instruktionen.belegmasse) || {};
    },

    // Die Obergrenze des Eingabefelds. null, solange nichts geladen ist — dann
    // steht kein max am Feld, und das Schema drüben fängt es ab. Eine Zahl
    // hier zu raten hieße, eine Grenze zu behaupten, die niemand gesetzt hat.
    belegbudgetMax() {
      const n = Number(this.belegmasse.budget_max);
      return Number.isFinite(n) ? n : null;
    },

    // Wie viele komplette Nachrichten der Bestandsweg liefert. Steht seit dem
    // 18.08. im Tooltip am Feld (vorher im Erklärtext darunter), damit dort
    // nicht "drei" steht, während OPTIMIZER_MAX_BELEGE auf etwas anderem steht.
    maxBelegeBackend() {
      const n = Number(this.belegmasse.max_belege);
      // ⚠️ null und NICHT 3. Der Kommentar darüber verlangt genau das: keine
      // Zahl behaupten, die niemand geliefert hat. Die geratene Drei stand
      // hier bis zum 18.08. und wanderte ohne jeden Vorbehalt in den Tooltip
      // — direkt daneben macht belegbudgetMax() es richtig.
      return Number.isFinite(n) ? n : null;
    },

    // Der Erklärtext am Feld. Gebunden und nicht als Konstante eingesetzt,
    // weil er die Zahl von oben trägt — dieselbe Quelle, ein Satz.
    belegbudgetHilfe() {
      return hilfeBelegbudget(this.maxBelegeBackend);
    },

    // "aus" | "null" | "budget" — welcher der drei Zustände gerade gilt.
    //
    // ACHTUNG, geprüft wird gegen "" und nicht auf Wahrheit: eine 0 ist falsy
    // und wäre sonst dasselbe wie ein leeres Feld — genau der Fehler, den
    // _ergaenzeKontextfelder in der Nachrichtenliste vermeidet.
    belegbudgetZustand() {
      const roh = String(this.belegbudget ?? "").trim();
      if (roh === "") return "aus";
      const n = Number(roh);
      if (!Number.isFinite(n)) return "aus";
      return n === 0 ? "null" : "budget";
    },

    // Der Wert, der über die Leitung geht — oder null.
    belegbudgetWert() {
      if (this.belegbudgetZustand === "aus") return null;
      return Number(String(this.belegbudget).trim());
    },

    // --- Modellauswahl ---------------------------------------------------

    // Nur, was einen Optimierer-Lauf tragen kann. Ein Einbettungsmodell
    // erzeugt keinen Text; stünde es in der Auswahl, quittierte der erste Lauf
    // damit mit einem Fehler, den niemand dem Modell zuordnet — die
    // Oberfläche hatte es ja angeboten.
    //
    // ⚠️ „unbekannt" bleibt DRIN. Das heißt nicht „ungeeignet", sondern
    // „Ollama hat auf die Nachfrage nicht geantwortet"; ein Modell deswegen zu
    // verstecken, nähme dem Menschen die Wahl aufgrund einer Störung.
    waehlbareModelle() {
      return (this.modelle || []).filter((m) => m.eignung !== "embedding");
    },

    // Steht der eingestellte Name in der Auswahl? Entscheidet, ob die Auswahl
    // ihn als eigenen Eintrag führen muss — ohne das säße sie sichtbar auf
    // einem Wert, den sie gar nicht führt, und `model` trüge einen anderen.
    modelInListe() {
      return (this.waehlbareModelle || []).some((m) => m.name === this.model);
    },

    // Der gewählte Eintrag, oder null bei einem selbst getippten Namen.
    gewaehltesModell() {
      return (this.modelle || []).find((m) => m.name === this.model) || null;
    },

    modellHilfe() {
      if (this.modelleFehler) {
        return (
          "Die Modellliste ist nicht erreichbar (" +
          this.modelleFehler +
          "). Zur Wahl steht deshalb nur der zuletzt eingestellte Name; er " +
          "geht unverändert an den Dienst."
        );
      }
      return (
        "Gefragt wird das Ollama des Rating-Dienstes — also das, welches den " +
        "Lauf fährt. Gewählt werden kann nur, was dort liegt: ein anderes " +
        "Modell vorher ziehen (ollama pull), danach steht es hier."
      );
    },

    // Was zum gewählten Modell zu melden ist — oder "".
    //
    // Leer heißt: nichts sagen. Gewarnt wird nur in zwei Lagen, und beide
    // kosten sonst einen fehlgeschlagenen Lauf:
    //   * der Name steht in keiner Liste (Tippfehler oder wirklich nicht da)
    //   * das Modell kann nicht denken, aber OPTIMIZER_THINK steht global auf
    //     true — dann antwortet Ollama mit HTTP 400, gemessen am 16.08.
    modellWarnung() {
      if (!this.modelleGeladen || !this.model) return "";
      const m = this.gewaehltesModell;
      if (!m) {
        return (
          "„" + this.model + "“ steht nicht in der Liste — das Modell liegt " +
          "beim Rating-Dienst nicht (mehr) vor. Der Dienst zieht es beim Lauf " +
          "nach; das kann dauern."
        );
      }
      if (this.thinkGlobal === true && !m.denkt) {
        return (
          "Dieses Modell kennt keinen Denkmodus, OPTIMIZER_THINK steht aber " +
          "auf 1. Der Lauf bricht dann mit einem Fehler des Dienstes ab."
        );
      }
      if (m.eignung === "unbekannt") {
        return (
          "Ollama hat zu diesem Modell keine Fähigkeiten gemeldet — ob es " +
          "einen Lauf trägt, ist offen."
        );
      }
      return "";
    },

    // --- Tokenmessung ----------------------------------------------------

    // Beide lesen nur, was der Dienst gemessen hat — hier wird nichts
    // gerechnet. Sobald diese Seite eine Tokenzahl selbst herleitete, gäbe es
    // zwei Stellen, an denen eine entsteht.
    messSatz() {
      return messsatz(this.messung);
    },

    messWarnung() {
      return messwarnung(this.messung);
    },

    messVorbehalt() {
      return messvorbehalt(this.messung);
    },

    // Messen geht nur gegen ein Modell, das drüben auch liegt. Der Dienst
    // lehnt sonst mit 409 ab — und zieht ausdrücklich NICHT nach, weil ein
    // Download von mehreren Gigabyte nicht hinter einen Messknopf gehört.
    //
    // ⚠️ Solange die Liste nicht geladen ist, wird NICHT gesperrt: dann ist
    // unbekannt, was vorliegt, und eine Sperre aufgrund von Unwissen nähme
    // dem Menschen eine Handlung, die funktioniert hätte.
    messenGesperrt() {
      if (this.proposing || this.messungLaeuft) return true;
      if (!this.model) return true;
      if (!this.modelleGeladen) return false;
      return !this.modelInListe;
    },

    // Der Grund steht als TEXT neben dem Knopf und nicht in einem Tooltip: an
    // einem `disabled`-Knopf erscheint in Chrome und Firefox nie einer — also
    // genau dann nicht, wenn jemand ihn braucht.
    messenGesperrtGrund() {
      if (!this.messenGesperrt) return "";
      if (this.proposing) return "Ein Lauf ist unterwegs.";
      if (this.messungLaeuft) return "";
      if (!this.model) return "Kein Modell gewählt.";
      // ⚠️ ZWEI Gründe, nicht einer. `modelInListe` prüft gegen die Auswahl
      // OHNE Einbettungsmodelle — stand `nomic-embed-text` im Feld, war
      // deshalb „liegt nicht vor" zu lesen, während `modellWarnung` daneben
      // schwieg, weil das Modell sehr wohl da ist. Zwei Aussagen über
      // denselben Zustand, und die sichtbare war falsch. Seit dem 19.08. ist
      // ein Einbettungsmodell nicht mehr wählbar, der Fall bleibt aber über
      // die Backend-Vorgabe (OPTIMIZER_MODEL) erreichbar.
      if (this.gewaehltesModell) {
        return "Ein Einbettungsmodell trägt keinen Optimierer-Lauf — " +
          "gemessen wird nur gegen ein Modell, das antworten kann.";
      }
      return "Dieses Modell liegt nicht vor — es wird zum Messen nicht geladen.";
    },

    merkAnzahl() {
      return this.merkliste.length;
    },

    // Die IDs, die beim Einfrieren mitgehen — leer, wenn der Schalter aus ist.
    merklisteIds() {
      return this.merklisteNutzen ? this.merkliste : [];
    },

    // Der Prompt, an den ein neuer Vorschlag angehängt wird — null im Modus ML
    // UND solange die Auskunft nicht geladen ist. Die beiden Fälle trennt das
    // Template über v-if="wirksam"; hier wären sie nicht zu unterscheiden.
    wirksamerSchluessel() {
      return this.wirksam?.prompt_key || null;
    },

    // Im Modus ML gibt es nichts fortzuschreiben.
    //
    // ACHTUNG: ausdrücklich `wirksam` vorausgesetzt. Ohne geladene Auskunft
    // wäre `!prompt_key` wahr, und der Knopf stünde bei jeder Störung des
    // Endpunkts gesperrt da — mit einer Begründung, die nicht zutrifft.
    keinPrompt() {
      return !!this.wirksam && !this.wirksam.prompt_key;
    },

    // Der TEXT der aktiven Version — die Grundlage der Maße daneben. Er kommt
    // aus derselben Auskunft wie der Schlüssel (effective-key); ein eigener
    // Endpunkt dafür wäre eine zweite Wahrheit über "was gilt gerade".
    aktiverPromptText() {
      return this.wirksam?.active_version_content || "";
    },

    // ACHTUNG: `=== false` und nicht `!`. Solange die Auskunft nicht geladen
    // ist (null), wird der Modus ANGEBOTEN — sonst stünde bei jeder Störung des
    // Endpunkts "abgeschaltet" da, und das ist eine Behauptung über eine
    // Umgebungsvariable, die niemand geprüft hat. Entschieden wird ohnehin
    // drüben; hier geht es nur darum, einen Fehlklick zu ersparen.
    reviseMoeglich() {
      return this.reviseEnabled !== false;
    },

    // Die Stelle, an der die Anweisung in den Rahmen kommt — aus der Auskunft
    // des Backends, nicht aus einer Konstante hier. Leer heißt "nicht
    // geladen", und dann wird nichts über sie behauptet.
    platzhalter() {
      return this.instruktionen?.platzhalter || "";
    },

    // Der Text, den der GEWÄHLTE Modus anhängt.
    //
    // ACHTUNG, `?.` bis zum Ende: die Auskunft ist eine Beigabe und darf
    // fehlen (das andere Backend kann aus sein). Fehlt sie, verschwindet der
    // Aufklapper — er behauptet dann nichts Falsches, statt einen leeren
    // Kasten zu zeigen.
    instruktionText() {
      return this.instruktionen?.instructions?.[this.modus]?.text || "";
    },

    instruktionHash() {
      const h = this.instruktionen?.instructions?.[this.modus]?.hash;
      return typeof h === "string" ? h.slice(0, 8) : "";
    },

    // Ob im Feld die Stelle fehlt, an der die Anweisung eingesetzt wird.
    //
    // ACHTUNG, geprüft wird der TEXT IM FELD und nicht die gespeicherte
    // Fassung: gewarnt werden muss, während jemand tippt — und zwar bevor er
    // speichert und den Lauf startet. Ohne geladenen Platzhalter wird nicht
    // gewarnt (`!!p`), sonst stünde die Warnung bei jeder Störung des
    // Endpunkts da.
    rahmenOhneStelle() {
      const p = this.platzhalter;
      return !!p && !(this.optimizerContent || "").includes(p);
    },

    // Die Notiz der aktiven Optimierer-Fassung, EINMAL gerechnet.
    //
    // ACHTUNG, warum ein Objekt und nicht zwei computed: „ist gekürzt?" und
    // „der gekürzte Text" sind dieselbe Rechnung. Getrennt gerechnet laufen
    // sie auseinander, sobald jemand die Grenze an einer der beiden Stellen
    // anfasst — dann trüge ein ungekürzter Text einen Tooltip, der nichts
    // Neues zeigt, oder ein gekürzter keinen. Dieselbe Regel wie bei den
    // Prompt-Maßen: eine Zahl, eine Quelle.
    //
    // `gekuerzt` wird aus dem VERGLEICH abgeleitet, nicht aus der Länge neu
    // bestimmt: so kann die Antwort gar nicht von dem abweichen, was
    // tatsächlich dasteht.
    optimizerNotiz() {
      const voll = (this.optimizerActive?.note || "").trim();
      const kurz =
        voll.length > NOTIZ_MAX_ZEICHEN
          ? voll.slice(0, NOTIZ_MAX_ZEICHEN).trimEnd() + "…"
          : voll;
      return { voll, kurz, gekuerzt: kurz !== voll };
    },

    regelnHoch() {
      return regelAnzahl(this.aktiverPromptText) >= REGELN_WARN;
    },

    zeichenHoch() {
      return promptZeichen(this.aktiverPromptText) >= PROMPT_WARN_ZEICHEN;
    },

    // Leer heißt: nichts sagen. Auf dem heutigen Bestand (1135 Zeichen,
    // 11 Regeln) ist das der Fall, und das ist Absicht — ein Hinweis, der immer
    // erscheint, wird nicht gelesen.
    promptWarnungText() {
      return promptWarnung(this.aktiverPromptText);
    },

    // ACHTUNG: nur wenn eine Diagnose da IST. Ohne geladene Antwort gäbe es
    // nichts zu entscheiden, und die Warnung fiele stillschweigend aus.
    //
    // ⚠️ Seit dem 10.08. entscheidet das BACKEND (diagnose.zu_wenig_material).
    // Die Schwelle hängt dort an OPTIMIZER_MIN_REFERENZZELLEN und ist je
    // Umgebung verstellbar — die Seite rechnete sie mit einer eigenen 10 nach
    // und wäre bei jedem anderen Wert still uneins mit dem Dienst gewesen, der
    // die Diagnose berechnet hat. Zwei Antworten auf "ist das genug?" sind eine
    // zu viel. Der Rückfall gilt für eingefrorene Diagnosen ohne das Feld.
    zuWenigMaterial() {
      if (!this.diagnose) return false;
      if (typeof this.diagnose.zu_wenig_material === "boolean") {
        return this.diagnose.zu_wenig_material;
      }
      return (this.diagnose.n_reference_cells || 0) < this.minReferenzZellen;
    },

    // Die Schwelle, wie sie der Dienst wirklich anwendet.
    minReferenzZellen() {
      const vom_dienst = this.diagnose && this.diagnose.min_reference_cells;
      return typeof vom_dienst === "number" ? vom_dienst : MIN_REFERENZ_ZELLEN;
    },

    // Steht im Feld ein Name, den es nicht gibt?
    //
    // ACHTUNG, die drei Bedingungen sind nicht redundant:
    //   * ohne evalSets.length wäre JEDER Name unbekannt, solange die Liste
    //     noch lädt oder der Endpunkt ausgefallen ist -- eine Warnung, die
    //     eine fremde Störung als Tippfehler des Benutzers ausgibt.
    //   * ohne den leeren Namen abzufangen stünde die Warnung sofort da,
    //     wenn jemand das Feld zum Neutippen leert.
    //   * getrimmt, weil ein angehängtes Leerzeichen im Feld unsichtbar ist,
    //     in set_name aber ein anderer Satz. Dieselbe Falle wie 'TEAM4 '.
    unbekannterSatz() {
      const name = (this.setName || "").trim();
      if (!name || !this.evalSets.length) return false;
      return !this.evalSets.some((s) => s.set_name === name);
    },

    // Warum ein neuer Satz gerade nicht eingefroren werden kann -- oder "".
    //
    // Der Namenskonflikt wird HIER abgefangen und nicht erst am 409 drüben:
    // der Aufruf schriebe zwar nichts, aber der Name steht schon im Feld, und
    // eine Sperre, die man sieht, bevor man klickt, ist der Unterschied
    // zwischen "geht nicht" und "ist schiefgegangen".
    neuerSatzBlockGrund() {
      const name = (this.newSetName || "").trim();
      if (!name) return "Der Satz braucht einen Namen.";
      if (this.evalSets.some((s) => s.set_name === name)) {
        return (
          `Den Satz „${name}“ gibt es bereits. Ein eingefrorener Satz wird nie ` +
          `nachgezogen — sonst zeigten frühere Gate-Ergebnisse auf eine andere ` +
          `Nachrichtenmenge.`
        );
      }
      if (!(this.newSetSize > 0)) return "Die Zielgröße muss mindestens 1 sein.";
      if (this.newSetMinRef < 0) return "Die Mindestzahl darf nicht negativ sein.";
      if (this.merklisteNutzen && this.merkAnzahl === 0) {
        return (
          "Die Merkliste ist leer. Gemerkt wird in der Nachrichtenliste " +
          "(Sternknopf an der Karte oder Taste M) — oder den Schalter ausmachen, " +
          "dann entsteht der Satz wie bisher aus allen korrigierten Nachrichten."
        );
      }
      if (this.merklisteNutzen && this.merkAnzahl > MERK_MAX) {
        return `Die Merkliste führt ${this.merkAnzahl} Nachrichten, erlaubt sind ${MERK_MAX}.`;
      }
      return "";
    },

    /**
     * Was am Einfrieren auffällt, ohne es zu sperren.
     *
     * ⚠️ Getrennt von neuerSatzBlockGrund, und das ist der Punkt: das hier
     * verhindert nichts. Eine Merkliste, die schon so groß ist wie die
     * Zielgröße, ist völlig in Ordnung — es wird dann eben nicht aufgefüllt,
     * und das soll nur niemanden überraschen.
     *
     * Was hier NICHT stehen kann: wie viele der gemerkten Nachrichten
     * überhaupt Korrekturen tragen. Das weiß nur das Backend (es filtert über
     * sechs Prädikate), und es hier zu schätzen hieße, eine zweite Zählung zu
     * pflegen. Reicht es nicht, kommt ein 409 mit beiden Zahlen.
     */
    neuerSatzHinweis() {
      if (!this.merklisteNutzen || this.merkAnzahl === 0) return "";
      if (this.merkAnzahl >= this.newSetSize) {
        return (
          `Die Merkliste ist mit ${this.merkAnzahl} Nachrichten schon so groß wie ` +
          `die Zielgröße (${this.newSetSize}) — aufgefüllt wird dann nicht.`
        );
      }
      return (
        `${this.merkAnzahl} gemerkte Nachrichten, aufgefüllt wird auf ` +
        `${this.newSetSize}. Wie viele davon Korrekturen tragen, entscheidet erst ` +
        `das Backend gegen die Mindestzahl.`
      );
    },

    optimizerDirty() {
      return this.optimizerContent.trim() !== this.optimizerGeladen.trim();
    },

    // Block 4 zeigt nur, was tatsächlich zu einer Version geworden ist. Ein
    // übernommener Vorschlag ohne prompt_version wäre eine leere Karte.
    adoptedProposals() {
      return this.proposals.filter((p) => p.prompt_version);
    },

    // Die Gegenmenge, und der Grund dafür: adoptedProposals ist eine Teilmenge
    // derselben Liste. Zeigte Block 3 alle Vorschläge, stünde jeder
    // übernommene zweimal auf demselben Schirm — beide Male mit dem vollen
    // Regeltext.
    offeneProposals() {
      return this.proposals.filter((p) => !p.prompt_version);
    },

    // Was ohne Klick dasteht: die VORSCHLAEGE_SICHTBAR jüngsten offenen
    // Vorschläge — oder alle, sobald jemand aufgeklappt hat.
    //
    // ⚠️ `slice(0, …)` und nicht `slice(-…)`: geschnitten wird VORNE, denn
    // vorne stehen die jüngsten (list_proposals sortiert nach id absteigend).
    // Vom anderen Ende genommen stünden die ältesten oben, und die Begrenzung
    // zählte genau die Vorschläge weg, wegen denen jemand die Seite öffnet.
    //
    // Gefiltert oder sortiert wird hier NICHTS mehr: das erledigt
    // offeneProposals, und eine zweite Filterung an dieser Stelle wäre eine
    // zweite Wahrheit darüber, was „offen" heißt.
    sichtbareProposals() {
      if (this.zeigeAeltereVorschlaege) return this.offeneProposals;
      return this.offeneProposals.slice(0, VORSCHLAEGE_SICHTBAR);
    },

    // Die Gegenmenge, und sie ist der REST DERSELBEN Liste — nicht eine
    // eigene Auswahl. Nur so ist die Zahl am Ausklapper die Zahl der Karten,
    // die er aufklappt.
    //
    // ⚠️ Hängt NICHT an zeigeAeltereVorschlaege: aufgeklappt bleibt der
    // Knopf stehen (er heißt dann „Ältere verbergen"), und mit einer leeren
    // Menge verschwände er unter dem Cursor.
    aeltereProposals() {
      return this.offeneProposals.slice(VORSCHLAEGE_SICHTBAR);
    },

    // Die Beschriftung des Ausklappers. Die Zahl steht IM aufklappenden Zweig
    // und kommt aus aeltereProposals — dieselbe Menge, die der Klick zeigt.
    // Selbst nachgerechnet (offeneProposals.length − 5) wäre sie eine zweite
    // Quelle für dieselbe Zahl.
    aeltereLabel() {
      return this.zeigeAeltereVorschlaege
        ? "Ältere verbergen"
        : `Ältere anzeigen (${this.aeltereProposals.length})`;
    },

    confirmTitle() {
      switch (this.pendingAction) {
        case "adopt":
          return "Vorschlag als Entwurf übernehmen";
        case "discard":
          return "Vorschlag verwerfen";
        default:
          return "";
      }
    },

    confirmLabel() {
      switch (this.pendingAction) {
        case "adopt":
          return "Übernehmen";
        case "discard":
          return "Verwerfen";
        default:
          return "";
      }
    },

    // ACHTUNG, warum das gesetzt werden MUSS: die Vorgabe von ConfirmModal ist
    // 'delete', also ein roter Knopf. Übernehmen erschien damit in der Farbe,
    // die auf dieser Oberfläche fürs Löschen steht — für eine Handlung, die
    // eine Prompt-Version ANLEGT und an keiner Bewertung etwas ändert. Die
    // Farbe soll die Folge anzeigen, nicht die Endgültigkeit.
    confirmVariant() {
      switch (this.pendingAction) {
        case "adopt":
          // Dieselbe Variante wie der auslösende Knopf in der Liste.
          return "switch";
        case "discard":
          return "delete";
        default:
          return "neutral";
      }
    },

    confirmMessage() {
      const p = this.pendingProposal;
      if (!p) return "";
      // ACHTUNG: der Dialog ist der eine Ort, an dem Text NICHT gekürzt wird,
      // bis die Folge unklar bleibt. Er steht vor einer Handlung, die eine
      // Prompt-Version schreibt, und wird genau einmal gelesen -- hier zu
      // sparen hiesse, die Folge zu verschweigen. Gekürzt ist deshalb nur,
      // was ohnehin an anderer Stelle steht.
      if (this.pendingAction === "adopt") {
        // „An der laufenden Bewertung ändert sich nichts" ist gestrichen: der
        // Satz danach sagt dasselbe schärfer und nennt zugleich die Bedingung.
        return (
          `Der Regeltext wird an den aktiven Bewertungsprompt gehängt — daraus ` +
          `entsteht eine Prompt-Version als Entwurf. Wirksam wird sie erst nach ` +
          `einem bestandenen Regressionsvergleich.\n\n` +
          `Regel:\n${p.suggestion}`
        );
      }
      if (this.pendingAction === "discard") {
        return (
          `Der Vorschlag wird auf „verworfen" gesetzt, nicht gelöscht — er ` +
          `bleibt nachvollziehbar stehen.\n\n` +
          `Regel:\n${p.suggestion}`
        );
      }
      return "";
    },
  },

  // ⚠️ Der wichtigste Teil der Tokenmessung — wichtiger als die Zahl selbst.
  //
  // Eine Messung gilt für GENAU DIE Parameter, mit denen sie entstanden ist.
  // Bliebe sie nach einer Änderung stehen, stünde neben „Budget 12" die Zahl
  // von Budget 6 — und niemand rechnet eine angezeigte Zahl nach. Dieselbe
  // Klasse wie die Belegpille, die am 17.08. entfernt wurde: eine Auskunft,
  // die einmal richtig war und deshalb nicht mehr gelesen wird.
  //
  // Alle vier Werte gehen in den Prompt ein: das Modell entscheidet über die
  // Tokenisierung, Modus und Budget über den Text, der Satzname über den
  // Befund.
  watch: {
    model() {
      this.messungVerwerfen();
    },
    modus() {
      this.messungVerwerfen();
    },
    belegbudget() {
      this.messungVerwerfen();
    },
    setName() {
      this.messungVerwerfen();
    },
  },

  mounted() {
    this.reload();
    // Getrennt von reload() und ohne await: die Liste kostet drueben
    // einen Aufruf je Modell, und ihr Ausfall darf die Seite nicht
    // aufhalten. Bis sie da ist, fuehrt die Auswahl allein den Namen, der
    // schon eingestellt ist — und der geht unveraendert an den Lauf.
    this.ladeModelle();
    // Die Merkliste wird beim Betreten gelesen und danach abonniert. Das
    // Abonnement ist nicht überflüssig, obwohl diese Seite nichts merkt: ein
    // zweiter Tab mit der Nachrichtenliste schreibt dieselbe Liste, und dann
    // stünde hier eine Zahl, die es nicht mehr gibt.
    this.merkliste = merklisteLesen();
    this.merkAbbestellen = merklisteAbonnieren((ids) => {
      this.merkliste = ids;
    });
  },

  beforeUnmount() {
    if (this.merkAbbestellen) this.merkAbbestellen();
  },

  methods: {
    // Hängt dieser Vorschlag an einem Prompt, der gerade nicht bewertet?
    //
    // ⚠️ Drei Bedingungen, keine davon überflüssig: ohne geladene Auskunft
    // wird nichts behauptet; ohne `p.prompt_key` ist es eine alte Zeile ohne
    // Herkunftsangabe (die Warnung träfe dann eine Zeile, über die man es
    // nicht weiß); und im Modus ML wirkt KEIN Prompt — dann steht die Aussage
    // schon oben und gehört nicht an jede Zeile ein zweites Mal.
    andererSchluessel(p) {
      if (!this.wirksam?.prompt_key || !p?.prompt_key) return false;
      return p.prompt_key !== this.wirksam.prompt_key;
    },

    // Die Maße des Prompts. Durchgereicht aus promptMass.js — das Template
    // braucht sie, und eine zweite Zählung hier wäre genau der Fehler, den
    // dieses Modul verhindert.
    regelAnzahl,
    promptZeichen,

    // ⚠️ NUR wenn er ABWEICHT. Bei einem Bestand, in dem jeder Vorschlag gegen
    // die aktive Version formuliert ist, wäre die Angabe an jeder Zeile eine
    // Angabe ohne Aussage — und die wird nicht gelesen.
    andereBasis(p) {
      const basis = p?.diagnose?.basis_prompt_version;
      const aktiv = this.wirksam?.active_version;
      if (typeof basis !== "number" || typeof aktiv !== "number") return false;
      // Nur beim eigenen Schlüssel: gehört der Vorschlag zum anderen Prompt,
      // steht das schon in der Pille daneben, und "aktiv ist v3" verglichen
      // eine Version mit einer aus einer anderen Reihe.
      if (this.andererSchluessel(p)) return false;
      return basis !== aktiv;
    },

    // "append" bleibt UNBESCHRIFTET: es ist der Normalfall und trägt jede
    // Bestandszeile. Eine Pille an jeder Zeile wäre wieder eine Angabe ohne
    // Aussage.
    artText(p) {
      const art = p?.vorschlag_art;
      const n = p?.ziel_regel;
      if (art === "revise" && n) return `ersetzt Regel ${n}`;
      if (art === "delete" && n) return `streicht Regel ${n}`;
      if (art === "unbrauchbar") return "Format verfehlt";
      return "";
    },

    artClass(p) {
      // ⚠️ Rot nur für "Format verfehlt". Es ist der eine Fall, in dem der Text
      // darunter keine Regel ist — wer ihn für eine hält, liest Modellprosa als
      // Vorschlag.
      if (p?.vorschlag_art === "unbrauchbar") {
        return "bg-red-100 text-red-800";
      }
      return "bg-indigo-100 text-indigo-800";
    },

    // Die ersten acht Zeichen des Meta-Prompt-Hashes. Er tritt an die Stelle
    // der Optimierer-Version, die ein Revise-Vorschlag nicht hat.
    regelpromptHash(p) {
      const hash = p?.diagnose?.revise_prompt_hash;
      return typeof hash === "string" ? hash.slice(0, 8) : "";
    },

    _fehlertext(e, fallback) {
      return String(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          fallback
      );
    },

    // Laufende Nummer gegen das Überholen. Wechselt jemand den Satznamen,
    // während ein Laden unterwegs ist, käme dessen Antwort womöglich NACH der
    // neuen und überschriebe die Diagnose mit der des alten Satzes.
    // Bewusst NICHT in data(): das Feld ist ein Zähler, kein Zustand der
    // Anzeige — reaktiv gemacht löste es bei jedem Laden ein Rendern aus.
    // --- Modellauswahl ---------------------------------------------------

    // Die Modelle laden. Bewusst NICHT Teil von reload():
    //
    //   * Die Liste kostet drüben einen Aufruf JE Modell (/api/show). Sie an
    //     jedes Neuladen der Diagnose zu hängen, hieße, sie bei jedem
    //     Satzwechsel neu zu ziehen.
    //   * Ihr Ausfall darf die Seite nicht kippen. Der Optimierer läuft auch
    //     ohne Liste — die Auswahl führt dann den zuletzt eingestellten Namen
    //     als einzigen Eintrag, und der geht unverändert an den Lauf.
    // --- Tokenmessung ----------------------------------------------------

    // Eine gültige Messung ist an ihre Parameter gebunden. Ändert sich einer,
    // ist sie keine Auskunft mehr über das, was jetzt eingestellt ist.
    messungVerwerfen() {
      // Die Messnummer zählt MIT hoch. Ohne das käme eine unterwegs
      // befindliche Antwort nach dem Verwerfen an und schriebe die Zahl
      // wieder hin — verworfen wäre sie dann nur für einen Wimpernschlag.
      this._messnr = (this._messnr || 0) + 1;
      this.messung = null;
      this.messungFehler = "";
      this.messungLaeuft = false;
    },

    async messeTokens() {
      if (this.messenGesperrt) return;
      // ⚠️ Messnummer, gleiches Muster wie `_ladenr` bei reload(). Die Messung
      // dauert warm 0,6 s, nach einem Ollama-Neustart aber bis zu 95 s — und
      // in dieser Zeit sind Modellfeld, Modus, Budget und Satzauswahl
      // weiterhin bedienbar. Ohne diese Nummer verwarf der watch-Block die
      // Zahl korrekt, und die eintreffende Antwort schrieb sie danach WIEDER
      // hin: eine falsche Zahl, die diesmal niemand angefordert hat.
      // messungVerwerfen() zählt mit hoch, deshalb genügt der Vergleich.
      const meine = (this._messnr = (this._messnr || 0) + 1);
      this.messungLaeuft = true;
      this.messungFehler = "";
      try {
        const a = await messeMetaPrompt(
          this.setName || null, this.model, this.modus, this.belegbudgetWert);
        if (meine !== this._messnr) return;
        this.messung = a?.data || null;
      } catch (e) {
        if (meine !== this._messnr) return;
        // 409 ist hier keine Störung, sondern eine Aussage über den Zustand:
        // entweder trägt der Satz keine Korrekturen, oder das Modell liegt
        // nicht vor. Beide Texte sind zum Lesen gemacht und werden unverändert
        // gezeigt.
        this.messung = null;
        this.messungFehler = this._fehlertext(
          e, "Die Tokenzahl konnte nicht gemessen werden");
      } finally {
        if (meine === this._messnr) this.messungLaeuft = false;
      }
    },

    async ladeModelle(frisch = false) {
      try {
        const a = await getOptimizerModels(frisch);
        this.modelle = a?.data?.models || [];
        this.thinkGlobal =
          typeof a?.data?.think_global === "boolean" ? a.data.think_global : null;
        this.modelleFehler = "";
        this.modelleGeladen = true;

        // ⚠️ Die Vorgabe kommt VON DORT und wird hier nur eingesetzt — und nur
        // in ein leeres Feld. Stünde sie als Literal in dieser Datei, wäre sie
        // die zweite Wahrheit neben OPTIMIZER_MODEL; überschriebe sie einen
        // getippten Namen, verlöre jemand mitten im Arbeiten seine Eingabe.
        if (!this.model && a?.data?.default) {
          this.model = a.data.default;
        }
      } catch (e) {
        // Kein this.error: die Seite ist nicht kaputt, nur diese eine Auskunft
        // fehlt. Der Grund steht im Tooltip am Feld, wo er gebraucht wird.
        this.modelle = [];
        this.modelleGeladen = false;
        this.modelleFehler = this._fehlertext(e);
      }
    },

    // ⚠️ Hier stand bis zum 19.08. modellGewaehlt(): die Auswahl schrieb in
    // ein Freitextfeld daneben, damit der Modellname an GENAU EINER Stelle
    // steht. Das Freitextfeld ist gestrichen, die Auswahl hält den Wert jetzt
    // selbst (v-model) — damit gibt es weiterhin genau eine Stelle, und einen
    // Handler dazwischen braucht es nicht mehr.

    async reload() {
      this.loading = true;
      this.error = "";
      const meine = (this._ladenr = (this._ladenr || 0) + 1);
      try {
        // Die Satzliste MIT laden und nicht nur beim ersten Aufschlagen: ein
        // Satz kann angelegt worden sein, seit die Seite offen steht, und wer
        // dann den richtigen Namen tippt, bekäme "Diesen Vergleichssatz gibt
        // es nicht" vorgehalten — eine Warnung, die selbst der Grund für die
        // Verwirrung wäre.
        // Der wirksame Schlüssel MIT: er sagt, welchen Prompt ein Vorschlag
        // fortschreibt. Sein Ausfall darf die Seite nicht kippen (siehe catch).
        const [d, o, v, s, w] = await Promise.all([
          getDiagnose(this.setName || null),
          getOptimizerPrompt(),
          listProposals(),
          listEvalSets(),
          getEffectivePromptKey().catch(() => null),
        ]);
        if (meine !== this._ladenr) return;

        this.diagnose = d?.data || null;
        this.evalSets = s?.data || [];
        this.wirksam = w?.data || null;

        const aktiv = o?.data?.active || null;
        this.optimizerActive = aktiv;
        this.optimizerVersions = o?.data?.versions || [];
        // Fehlt die Beigabe, bleibt sie null — nicht {} und nicht eine leere
        // Fassung. Der Unterschied ist der zwischen "nicht geladen" und "es
        // gibt keine Anweisung", und nur der erste trifft zu.
        this.instruktionen = o?.data?.instructions || null;
        // Das Belegbudget startet nicht mehr leer, sondern auf der Zahl, die
        // der Optimierer ohne Eingabe ohnehin heranzieht
        // (belegmasse.max_belege, Vorgabe drei).
        //
        // ACHTUNG, das ist eine VERHALTENSAENDERUNG und keine Anzeige: mit
        // gesetztem Budget verteilt der Lauf die Belege nach Rang ueber ALLE
        // Befunde, das leere Feld nimmt sie aus dem groessten. Die beiden
        // fallen nicht zusammen -- deshalb bleibt das Feld leerbar, und
        // geleert bleibt es auch: vorbelegt wird genau EINMAL je Seitenaufruf,
        // sonst holte ein Reload nach jedem Vorschlag die Zahl still zurueck.
        //
        // ACHTUNG, die Zahl kommt ueber die Leitung. Fehlt die Angabe (aelteres
        // Backend, Ausfall), bleibt das Feld leer -- geraten wird nichts,
        // derselbe Grund wie bei belegbudgetMax und maxBelegeBackend.
        if (!this._belegbudgetVorbelegt) {
          const vorgabe = this.maxBelegeBackend;
          if (vorgabe !== null) {
            this._belegbudgetVorbelegt = true;
            this.belegbudget = String(vorgabe);
          }
        }
        // Nur überschreiben, wenn im Feld nichts Eigenes steht. Sonst wäre ein
        // Reload mitten im Schreiben ein stiller Verlust des Entwurfs.
        if (!this.optimizerDirty) {
          this.optimizerContent = aktiv?.content || "";
        }
        this.optimizerGeladen = aktiv?.content || "";
        // ⚠️ Nur setzen, wenn das Feld WIRKLICH kam. Ein älteres Backend
        // liefert es nicht, und `|| false` machte daraus die Behauptung
        // "abgeschaltet" — über eine Umgebungsvariable, die niemand gelesen
        // hat. null heißt weiterhin "unbekannt", und unbekannt bietet an.
        this.reviseEnabled =
          typeof o?.data?.revise_enabled === "boolean" ? o.data.revise_enabled : null;
        // Und der Umschalter darf nicht auf einem Modus stehen bleiben, den es
        // nicht gibt: sonst liefe der nächste Klick in einen 409, den die Seite
        // eine Zeile höher selbst angekündigt hat.
        if (this.reviseEnabled === false && this.modus === "revise") {
          this.modus = "append";
        }

        this.proposals = v?.data || [];
      } catch (e) {
        if (meine !== this._ladenr) return;
        this.error = this._fehlertext(e, "Die Reflektionsdaten konnten nicht geladen werden");
      } finally {
        if (meine === this._ladenr) this.loading = false;
      }
    },

    oeffneNeuenSatz() {
      this.showNewSet = !this.showNewSet;
      if (this.showNewSet && !this.newSetName) {
        this.newSetName = this._freierName();
      }
    },

    // Ein Namensvorschlag, der noch nicht vergeben ist. Kein Komfort um
    // seiner selbst willen: der häufigste Fehlversuch ist, denselben Namen
    // noch einmal zu nehmen, und der endet drüben in einem 409, weil ein
    // eingefrorener Satz nicht nachgezogen wird.
    _freierName() {
      const namen = new Set(this.evalSets.map((s) => s.set_name));
      for (let i = 1; i <= 99; i += 1) {
        const kandidat = `korrektur-${String(i).padStart(2, "0")}`;
        if (!namen.has(kandidat)) return kandidat;
      }
      return "";
    },

    // ACHTUNG, zweierlei:
    //
    // (1) Das Protokoll des Backends wird NICHT verschwiegen — es führt die
    //     Filterzähler, also die Auskunft darüber, was beim Einfrieren
    //     ausgelassen wurde.
    // (2) Ein Satz unter der Zielgröße ist KEIN glatter Erfolg: unter 20
    //     Nachrichten ist missing_delta instabil, weil eine ausgefallene
    //     Nachricht 1/N ist. Grün eingefärbt läse sich das als "hat geklappt",
    //     und der nächste Vergleich fiele aus einem Grund durch, der hier
    //     schon dastand. Gelesen wird dafür das FELD unter_ziel und nicht der
    //     Wortlaut einer Logzeile — Logzeilen formuliert man um.
    async createSet() {
      if (this.neuerSatzBlockGrund) return;
      this.creatingSet = true;
      this.error = "";
      this.savedMessage = "";
      this.hinweis = "";
      this.letztesProtokoll = [];
      try {
        const name = this.newSetName.trim();
        const res = await createEvalSet(name, {
          auffuellenAuf: this.newSetSize,
          minReferenz: this.newSetMinRef,
          messageIds: this.merklisteIds,
        });
        const d = res?.data || {};
        // ⚠️ Das Protokoll wird als LISTE gezeigt, nicht mit join(" ") in einen
        // Satz gequetscht. Seit dem Merklistenweg führt es id-genaue Auskünfte
        // („Nachricht 312 nicht eingefroren“) — und die sind genau das, was
        // niemand aus einem Fließtext heraussucht, während der Satz schon
        // eingefroren ist.
        this.letztesProtokoll = Array.isArray(d.protokoll) ? d.protokoll : [];
        const kopf =
          `Satz „${d.set_name || name}“ eingefroren: ${d.n_messages ?? "?"} Nachrichten.`;
        if (d.unter_ziel) {
          this.hinweis =
            `${kopf} Er bleibt damit unter der Zielgröße — bei weniger als 20 ` +
            `Nachrichten ist missing_delta instabil (eine ausgefallene ist 1/N).`;
        } else {
          this.savedMessage = kopf;
        }

        this.setName = d.set_name || name;
        this.showNewSet = false;
        this.newSetName = "";
        await this.reload();
      } catch (e) {
        this.error = this._fehlertext(e, "Der Vergleichssatz ließ sich nicht anlegen");
      } finally {
        this.creatingSet = false;
      }
    },

    // ACHTUNG, der Grund für die Fallunterscheidung: put_optimizer_prompt hat
    // ZWEI Erfolgszweige (reflection.py:338-376). Ist der Text mit einer
    // bekannten, aber inaktiven Fassung identisch, wird KEINE neue Version
    // angelegt — jene alte Zeile wird wieder aktiv, die Versionsnummer springt
    // dabei zurück, und payload.note wird in diesem Zweig nie verwendet.
    // Bis zum 09.08. meldete die Oberfläche auch dann "gespeichert" und leerte
    // das Notizfeld: die Notiz war weg, und niemand konnte es sehen.
    async saveOptimizer() {
      this.savingOptimizer = true;
      this.error = "";
      this.savedMessage = "";
      this.hinweis = "";
      // Vor dem Aufruf festhalten, welche Fassungen es schon gab. Danach ist
      // die Liste neu geladen und die Frage nicht mehr zu beantworten.
      const bekannt = new Set(this.optimizerVersions.map((z) => z.version));
      const hatteNotiz = !!this.optimizerNote.trim();
      try {
        const res = await saveOptimizerPrompt(this.optimizerContent, this.optimizerNote);
        const v = res?.data?.version;
        if (bekannt.has(v)) {
          this.savedMessage =
            `Diesen Text gab es schon: Version ${v} ist wieder aktiv. Es wurde keine ` +
            `neue Version angelegt` +
            (hatteNotiz ? ", und die Notiz wurde nicht übernommen." : ".");
        } else {
          this.savedMessage =
            `Optimierer-Prompt v${v ?? "?"} ist gespeichert und aktiv. ` +
            `Jetzt „Vorschlag erzeugen“.`;
        }
        this.optimizerNote = "";
        // ⚠️ Die Messung gilt NICHT mehr. Der gespeicherte Text ist ab jetzt
        // die aktive Optimierer-Version, und genau die baut drüben den
        // Meta-Prompt — sie ist die fünfte Eingangsgröße neben Modell, Modus,
        // Budget und Satz. Der watch-Block sieht nur die vier Formularfelder;
        // ohne diese Zeile bliebe neben einem um dreißig Regeln gewachsenen
        // Prompt die Zahl des alten stehen, und niemand rechnet sie nach.
        this.messungVerwerfen();
        await this.reload();
      } catch (e) {
        // 409 ist keine Panne, sondern die Auskunft, dass dieser Text schon
        // einmal gespeichert wurde. Der Satz ist zum Lesen gemacht.
        this.error = this._fehlertext(e, "Der Meta-Prompt ließ sich nicht speichern");
      } finally {
        this.savingOptimizer = false;
      }
    },

    // ACHTUNG: SYNCHRON. Der Aufruf wartet auf den Optimierer-Lauf (30–90 s),
    // deshalb ist der Knopf währenddessen gesperrt und der Ladezustand
    // sichtbar. Ein zweiter Klick startete einen zweiten Lauf.
    async propose() {
      this.proposing = true;
      this.error = "";
      this.savedMessage = "";
      this.hinweis = "";
      try {
        const res = await proposeRule(
            this.setName || null, this.model, this.modus, this.belegbudgetWert);
        const p = res?.data || null;
        if (p?.vorschlag_art === "unbrauchbar") {
          // ⚠️ WEDER grün NOCH rot. Der Lauf ist durch, das Modell hat
          // geantwortet — nur nicht in der verlangten Form. Grün läse sich als
          // "hat geklappt" und der Text darunter als Regel; rot schickte
          // jemanden die Dienste prüfen, die einwandfrei gelaufen sind.
          this.hinweis =
            "Der Lauf ist durch, aber das Modell hat das Antwortformat verfehlt. " +
            "Der Vorschlag steht unten mit der rohen Antwort und ist nicht " +
            "übernehmbar — ein neuer Lauf ist der nächste Schritt.";
        } else if (p) {
          this.savedMessage = `Ein neuer Vorschlag liegt vor (Modell ${p.model}).`;
        } else {
          // ACHTUNG: KEIN grüner Kasten. Ein Lauf ohne Regel ist kein Erfolg
          // und kein Fehler — das Backend kennt die Antwort "NO RULE"
          // ausdrücklich als Ergebnis. Grün eingefärbt läse sich das als
          // "hat geklappt", und der nächste Griff ginge zum falschen Hebel.
          this.hinweis =
            "Der Lauf ist durch, ohne Regel. Entweder gab der Befund keine her, oder " +
            "die Anweisung trifft nicht — die Cluster oben sind der Prüfstein.";
        }
        await this.reload();
      } catch (e) {
        // 409: keine Referenzzellen im Satz. 502: Rating-Service oder Ollama
        // nicht erreichbar. Beide Texte kommen im Klartext und werden
        // unverändert gezeigt.
        this.error = this._fehlertext(e, "Der Vorschlag ließ sich nicht erzeugen");
      } finally {
        this.proposing = false;
      }
    },

    askDiscard(p) {
      this.pendingProposal = p;
      this.pendingAction = "discard";
      this.showConfirm = true;
    },

    // ACHTUNG, warum hier nachgeladen wird und nicht erst beim Absenden:
    // der Dialog nennt Regeltext und Folge und wird genau einmal gelesen. Ihn
    // über einer veralteten Vorschau zu öffnen hieße, die Bestätigung auf eine
    // Aussage zu holen, die schon nicht mehr stimmt.
    //
    // Die Arbeitsteilung: HIER wird dafür gesorgt, dass das Geprüfte frisch
    // ist; dass das Geprüfte auch das Übernommene ist, sichert das Backend
    // über den mitgeschickten vorschau_hash. Das Restfenster zwischen diesem
    // Klick und dem POST deckt allein der 409 ab — ein Polling über einem
    // offenen Modal wäre Aufwand ohne Gegenwert.
    //
    // Bewusst NICHT reload(): das setzt loading = true, und dann verschwindet
    // die Liste unter dem Cursor; außerdem leerte es error und fasste den
    // Optimierer-Entwurf an, an dem hier niemand etwas will.
    async askAdopt(p) {
      this.error = "";
      this.pruefeId = p.id;
      // Dieselbe laufende Nummer wie reload(): ein später begonnenes Laden
      // gewinnt. Ohne sie überschriebe die Antwort von hier eine frischere
      // Liste — der Frischecheck machte die Anzeige dann selbst alt.
      const meine = (this._ladenr = (this._ladenr || 0) + 1);
      try {
        const [v, w] = await Promise.all([
          listProposals(),
          getEffectivePromptKey().catch(() => null),
        ]);
        if (meine !== this._ladenr) return;
        this.proposals = v?.data || [];
        // Muss mit: andereBasis(p) und die Prompt-Maße daneben lesen aus
        // `wirksam`. Ohne das stünde nach dem Nachladen „aktiv ist v9" mit
        // einer veralteten 9 neben einer frischen Liste.
        if (w?.data) this.wirksam = w.data;

        const frisch = this.proposals.find((z) => z.id === p.id) || null;
        if (!frisch) {
          this.frischHinweis = { ...this.frischHinweis, [p.id]: HINWEIS_ZEILE_WEG };
          return;
        }

        // Verglichen werden ZWEI vom Backend gelieferte Werte. Hier wird
        // nichts nachgerechnet — weder der Text noch der Hash noch die Sperre;
        // die einzige eigene Aussage lautet „was hier stand, steht nicht
        // mehr". Rechnete die Seite die Sperre nach, gäbe es zwei Antworten
        // auf dieselbe Frage.
        //
        // adopt_block_reason gehört mit in den Vergleich: der Text kann gleich
        // bleiben und die Karte trotzdem unübernehmbar werden, wenn jemand
        // anders genau diesen Text inzwischen als Version angelegt hat. Der
        // Hash wäre dann unverändert und der POST liefe in einen 409.
        if (frisch.vorschau_hash !== p.vorschau_hash
            || frisch.adopt_block_reason !== p.adopt_block_reason) {
          this.frischHinweis = {
            ...this.frischHinweis, [p.id]: HINWEIS_VORSCHAU_GEWECHSELT,
          };
          // Den neuen Text auch zeigen. Ein Satz „der Text hat sich geändert"
          // ohne den Text ist eine Behauptung, die niemand prüfen kann.
          this.expandedVorschau = { ...this.expandedVorschau, [p.id]: true };
          return;
        }

        this.frischHinweis = { ...this.frischHinweis, [p.id]: "" };
        // Die FRISCHE Zeile in den Dialog, nicht die geklickte: übernommen
        // wird gegen den Stand, der gerade geprüft worden ist.
        this.pendingProposal = frisch;
        this.pendingAction = "adopt";
        this.showConfirm = true;
      } catch (e) {
        // Kein Rückfall auf „trotzdem öffnen". Dieselbe Regel wie der fehlende
        // default-Zweig in confirmed(): Übernehmen ist die folgenreichere
        // Handlung, und ein unbekannter Zustand führt deshalb zu gar nichts.
        this.error = this._fehlertext(
          e, "Die Vorschau ließ sich nicht auf Frische prüfen — übernommen wurde nichts",
        );
      } finally {
        // ACHTUNG, die Bedingung hängt an der id und NICHT an `meine ===
        // this._ladenr`. Mit der Ladenummer bliebe der Knopf dauerhaft auf
        // „Prüfe…" und damit gesperrt, sobald irgendein reload() diesen Lauf
        // überholt — die Karte wäre unbedienbar. So räumt immer der letzte
        // Setzer auf; schlimmstenfalls ist der Knopf einen Wimpernschlag zu
        // früh wieder frei, und das ist der harmlosere der beiden Fehler.
        if (this.pruefeId === p.id) this.pruefeId = null;
      }
    },

    cancelConfirm() {
      this.showConfirm = false;
      this.pendingProposal = null;
      this.pendingAction = null;
    },

    // Ein Einstieg für zwei Handlungen. Die Verzweigung steht hier und nicht
    // im Template, damit das Modal nicht raten muss, was "confirm" bedeutet.
    //
    // ACHTUNG: kein default auf "adopt". Übernehmen ist die folgenreichere der
    // beiden Handlungen — sie schreibt eine neue Prompt-Version. Sie als
    // Rückfall für jeden unbekannten oder bereits geleerten pendingAction zu
    // nehmen hieße: im Zweifel das Schärfste tun. Ein unbekannter Zustand
    // führt deshalb zu gar nichts.
    confirmed() {
      switch (this.pendingAction) {
        case "adopt":
          return this.adoptConfirmed();
        case "discard":
          return this.discardConfirmed();
        default:
          this.cancelConfirm();
          return;
      }
    },

    async discardConfirmed() {
      const p = this.pendingProposal;
      this.showConfirm = false;
      this.pendingAction = null;
      if (!p) return;

      this.discardingId = p.id;
      this.error = "";
      this.savedMessage = "";
      this.hinweis = "";
      try {
        await discardProposal(p.id);
        // Nicht p.id: die Zeilennummer steht nirgends auf dieser Seite, und
        // eine Quittung, die auf eine unsichtbare Zahl zeigt, quittiert nichts.
        this.savedMessage =
          `Der Vorschlag vom ${this.formatTs(p.created_at)} ist verworfen — er steht ` +
          `weiterhin in der Liste, jetzt als „verworfen“.`;
        await this.reload();
      } catch (e) {
        this.error = this._fehlertext(e, "Verwerfen fehlgeschlagen");
      } finally {
        this.discardingId = null;
        this.pendingProposal = null;
      }
    },

    async adoptConfirmed() {
      const p = this.pendingProposal;
      this.showConfirm = false;
      this.pendingAction = null;
      if (!p) return;

      this.adoptingId = p.id;
      this.error = "";
      this.savedMessage = "";
      this.hinweis = "";
      try {
        // Der Anker geht MIT: er sagt dem Backend, gegen welche Vorschau
        // geklickt wurde, und das rechnet ihn neu. Ihn dort zu erraten wäre
        // unmöglich — die Frage lautet ja gerade, was der Mensch vor sich
        // hatte, und das weiß nur diese Seite. Gerechnet wird er trotzdem
        // nicht hier: er kommt aus der Liste und geht unverändert zurück.
        const res = await adoptProposal(p.id, p.vorschau_hash);
        const v = res?.data?.version;
        // ACHTUNG, warum die Meldung MITGEGEBEN und nicht hier gesetzt wird:
        // der router.push direkt danach zerstört diese Komponente, und mit ihr
        // savedMessage. Bis zum 09.08. stand hier ein Satz, den niemand je zu
        // sehen bekam — und er sagte "der Vergleich wird HIER gestartet",
        // während "hier" schon die Seite war, auf der er nicht mehr steht.
        // ⚠️ Der dritte Satz ist seit dem 13.08. dabei, und er sagt das
        // Kontraintuitive: der frische Entwurf ist NICHT die Basis des
        // nächsten Vorschlags. erzeuge_vorschlag liest active_prompt_version()
        // mit hartem is_active-Filter, der Entwurf steht auf 'draft' und
        // is_active=false. Ohne den Satz übernimmt jemand zweimal
        // hintereinander und wundert sich, warum die zweite Regel die erste
        // nicht kennt.
        // Die aktive Nummer nur nennen, wenn sie geladen ist — ohne `wirksam`
        // wäre "auf v?" eine Behauptung über einen Zustand, den die Seite
        // nicht kennt. Dann bleibt der allgemeine Satz, der auch ohne Zahl
        // stimmt.
        const aktiv = this.wirksam?.active_version;
        const basis = aktiv
          ? `Bis v${v ?? "?"} bestanden und aktiviert ist, baut jeder weitere Vorschlag weiter auf v${aktiv} auf.`
          : `Bis v${v ?? "?"} bestanden und aktiviert ist, baut jeder weitere Vorschlag weiter auf der aktiven Version auf.`;
        const meldung =
          `Aus dem Vorschlag ist Version ${v ?? "?"} als Entwurf geworden. ` +
          `Gemessen ist damit nichts — jetzt unten bei v${v ?? "?"} den Vergleich starten. ` +
          basis;
        // Bewusst weiterleiten statt hier einen zweiten Vergleichsknopf zu
        // bauen: der Vergleich ist auf der Versionsseite zuhause, und zwei
        // Bedienstellen für dieselbe Sache sind zwei Gelegenheiten, sie
        // unterschiedlich falsch zu machen.
        this.$router.push({ path: "/async/prompt-versions", query: { hinweis: meldung } });
      } catch (e) {
        // 409: schon übernommen, oder der Text existiert bereits als Version.
        // detail nennt dann Version und Gate-Ergebnis.
        this.error = this._fehlertext(e, "Übernehmen fehlgeschlagen");
      } finally {
        this.adoptingId = null;
        this.pendingProposal = null;
      }
    },


    toggleProposal(id) {
      this.expandedProposal = { ...this.expandedProposal, [id]: !this.expandedProposal[id] };
    },


    // Der Tooltip am Kriteriumsnamen: Schlüssel und, wenn es eine gibt, die
    // Beschreibung. Sie ist DIESELBE, die der Optimierer im Befund sieht —
    // gekürzt und einzeilig gemacht hat sie das Backend, hier wird nichts
    // nachgerechnet.
    //
    // ⚠️ Im Bestand haben 9 von 20 Kriterien eine Beschreibung, und
    // „Adressatengerecht" hat keine. Ohne das v-if-lose Zusammensetzen stünde
    // dort dann ein leerer Absatz.
    kriteriumHilfe(c) {
      const schluessel = `Schlüssel: ${(c && c.criterion_key) || "?"}`;
      const beschreibung = (c && c.criterion_description) || "";
      return beschreibung ? `${schluessel}\n\n${beschreibung}` : schluessel;
    },

    toggleVorschau(id) {
      this.expandedVorschau = { ...this.expandedVorschau, [id]: !this.expandedVorschau[id] };
    },

    // Die EINZIGE Stelle, die zeigeAeltereVorschlaege umsetzt. Kein reload und
    // keine Aktion an einer Karte fasst ihn an — s. den Kommentar in data().
    toggleAeltereVorschlaege() {
      this.zeigeAeltereVorschlaege = !this.zeigeAeltereVorschlaege;
    },

    // Der Vorschautext als Zeilen, jede mit der Auskunft, ob sie neu ist.
    //
    // ⚠️ Die Marken kommen als Zeilenindizes aus dem Backend und beziehen sich
    // auf dessen splitlines(). Deshalb wird hier nach denselben Trennern
    // zerlegt UND eine leere Schlusszeile verworfen: "a\nb\n" ergibt in Python
    // zwei Zeilen, in JavaScript drei — ohne das pop() zeigte die Vorschau
    // eine Leerzeile, die es im Prompt nicht gibt, und alle Marken dahinter
    // säßen richtig, aber die Anzeige wäre eine Zeile länger als der Text.
    //
    // ⚠️ Die Trennermenge ist die von Pythons splitlines und ausdrücklich
    // NICHT die naheliegende `/\r\n|\n|\r/`. Python trennt zusätzlich bei
    // \v \f \x1c \x1d \x1e \x85 \u2028 \u2029 — und \u2028 kommt beim Einfügen
    // aus Word oder PDF in das Prompt-Feld wirklich vor. Zerlegte man hier
    // gröber, wäre die Zeilenzählung um jedes solche Zeichen versetzt, und die
    // Marke säße auf einer Regel, die niemand angefasst hat.
    vorschauZeilen(p) {
      const roh = String((p && p.vorschau_text) || "")
        // Die Steuerzeichen sind hier der Gegenstand: an genau ihnen
        // trennt Pythons splitlines, und die Marken sind dessen
        // Zeilenindizes. Die Regel warnt zu Recht -- nur eben nicht hier.
        // eslint-disable-next-line no-control-regex
        .split(/\r\n|[\n\r\v\f\x1c\x1d\x1e\x85\u2028\u2029]/);
      if (roh.length && roh[roh.length - 1] === "") roh.pop();
      const marken = new Set((p && p.vorschau_marken) || []);
      return roh.map((text, i) => ({ text, neu: marken.has(i) }));
    },

    // Der Rohwert bleibt der Rückfall: ein unbekannter Status kommt als
    // Zeichenkette aus der Datenbank, und ein leeres Feld wäre schlimmer als
    // ein englisches Wort.
    statusText(status) {
      return STATUS_TEXT[status] || status;
    },

    gateStatusHilfe(status) {
      return GATE_STATUS_HILFE[status] || "Zustand des Vergleichs: " + status;
    },

    // Warum "Verwerfen" gerade nicht geht — als sichtbarer Text, aus demselben
    // Grund wie adopt_block_reason: an einem gesperrten Knopf erscheint nie
    // ein Tooltip. Anders als dort berechnet das Backend den Grund nicht, weil
    // es den Knopf nicht kennt; die Bedingung ist aber dieselbe wie in
    // discard_proposal (nur status == 'neu' ist verwerfbar).
    discardBlockReason(p) {
      if (!p || p.status === "neu") return "";
      if (p.status === "uebernommen") {
        return "Bereits übernommen — verwerfen würde die Herkunft der Version verwischen.";
      }
      if (p.status === "verworfen") return "Bereits verworfen.";
      return "Nur ein neuer Vorschlag lässt sich verwerfen.";
    },

    // Ein unbekannter Status ist möglich — er kommt als Zeichenkette aus der
    // Datenbank, nicht als Aufzählung im Frontend.
    statusClass(status) {
      switch (status) {
        case "uebernommen":
        case "passed":
          return "bg-green-100 text-green-700";
        case "verworfen":
        case "failed":
          return "bg-red-100 text-red-700";
        case "neu":
          return "bg-blue-100 text-blue-700";
        case "running":
          return "bg-amber-100 text-amber-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    },

    formatTs(wert) {
      if (!wert) return "—";
      const d = new Date(wert);
      if (Number.isNaN(d.getTime())) return wert;
      return d.toLocaleString();
    },

    formatZahl(wert) {
      if (wert === null || wert === undefined) return "—";
      if (typeof wert !== "number") return String(wert);
      return Number.isInteger(wert) ? String(wert) : wert.toFixed(4);
    },

    /**
     * Die Belegnachrichten eines Vorschlags, jede mit ihrem Gesprächsverlauf.
     *
     * Sie liegen im eingefrorenen Befund (diagnose.cluster[0].beispiele_kontext)
     * und werden seit Befund B2 (11.08.) mitgeschrieben. Ein Vorschlag von
     * VORHER hat das Feld nicht — er bekommt in der Anzeige den Vermerk statt
     * der Texte, denn für ihn gilt die Begründung vom 10.08. unverändert:
     * Chat-Nachrichten ohne ihren Verlauf sind der Ausschnitt, aus dem ein
     * Mensch nichts schließen soll.
     *
     * ACHTUNG, gelesen wird ausschließlich cluster[0]: der Optimierer bekommt
     * die Belege nur zum größten Cluster (MAX_BELEGE aus reflection.py). Eine
     * Anzeige, die über alle Cluster suchte, zeigte Texte, die nie in einem
     * Meta-Prompt standen — und behauptete damit eine Grundlage, die es nicht
     * gab.
     */
    /**
     * Wie viele der korrigierten Zellen den Modellwert BESTÄTIGT haben — oder 0.
     *
     * ⚠️ Das ist die Zahl, die `n_reference_cells` von der Zahl der Zellen mit
     * echtem Befund trennt. Sie steht als `n_unchanged` im eingefrorenen Befund
     * (reflection.py:779-781: gezählt wird, wenn `modell == mensch`, und die
     * Zeile geht dann in KEINEN Cluster).
     *
     * ⚠️ Gelesen wird ausdrücklich `n_unchanged` und NICHT die Differenz zu
     * `beleg_zellen.length`. Beide Wege ergeben im Bestand dasselbe (an 42 von
     * 42 geprüft), aber `n_unchanged` ist die Quelle und `beleg_zellen` die
     * Folge — eine Zahl, eine Quelle. Über `beleg_zellen` zu rechnen hieße
     * außerdem, sich an ein Feld zu binden, das für den Rückweg zu den
     * Korrekturen da ist und nicht für eine Anzeige.
     *
     * 0 wird nicht angezeigt: es gibt dann nichts zu unterscheiden. Ein Satz,
     * der bei jedem Vorschlag „alle" sagt, ist Ballast.
     */
    bestaetigteZellen(vorschlag) {
      const n = ((vorschlag || {}).diagnose || {}).n_unchanged;
      return typeof n === "number" && n > 0 ? n : 0;
    },

    belegBloecke(vorschlag) {
      const diagnose = (vorschlag || {}).diagnose || {};
      // Zuerst die oberste Ebene: dort liegen die Blöcke, sobald ein
      // Belegbudget gefahren wurde — sie gehören dann nicht mehr dem größten
      // Cluster allein. Erst danach die alte Stelle, für jeden Vorschlag, der
      // vor dieser Änderung entstanden ist.
      if (Array.isArray(diagnose.beispiele_kontext)) {
        return diagnose.beispiele_kontext;
      }
      const cluster = diagnose.cluster || [];
      return (cluster[0] || {}).beispiele_kontext || [];
    },

    /**
     * Woher die Belege eines Vorschlags stammen — als Satzteil.
     *
     * Bis zu dieser Änderung stand im Template fest „aus dem größten Cluster“.
     * Das stimmt für jeden Vorschlag ohne Budget weiterhin und für keinen mit.
     * Die Zahl der Befunde kommt aus dem eingefrorenen Befund; fehlt sie, wird
     * die alte Formulierung gezeigt — sie war für diese Vorschläge richtig.
     */
    belegHerkunft(vorschlag) {
      const diagnose = (vorschlag || {}).diagnose || {};
      const belege = diagnose.belege;
      if (!Array.isArray(belege) || !belege.length) {
        return "aus dem größten Cluster";
      }
      const befunde = new Set(belege.map((b) => (b || {}).cluster)).size;
      return befunde === 1
          ? "aus einem Befund"
          : `aus ${befunde} Befunden`;
    },

    /**
     * Wie viele dieser Belege wirklich einen Verlauf führen — oder null.
     *
     * null heißt "steht nicht im Befund" und ist NICHT dasselbe wie 0. Eine
     * fehlende Angabe als 0 auszugeben hieße zu behaupten, keiner der Belege
     * habe einen Verlauf gehabt; das ist die schärfere Aussage, und sie wäre
     * unbelegt. Das Template lässt den Zusatz dann weg.
     */
    belegeMitVerlauf(vorschlag) {
      const n = ((vorschlag || {}).diagnose || {}).n_belege_mit_kontext;
      return typeof n === "number" ? n : null;
    },

    metricRows(metrics) {
      const zeilen = [];
      for (const k of ZIEL_KENNZAHLEN) {
        const wert = metrics?.[k.name];
        if (wert === undefined) continue;
        zeilen.push({
          name: k.name,
          hilfe: k.hilfe,
          value: this.formatZahl(wert),
        });
      }
      return zeilen;
    },
  },
};
</script>
